import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Send,
  FileText,
  CheckCircle2,
  CheckCircle,
  Edit3,
  AlertTriangle,
  Eye,
  ExternalLink,
  HelpCircle,
  UtensilsCrossed,
  Wine,
  Plus,
  Trash2,
  TrendingUp,
  TrendingDown,
  ListChecks,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  History,
  ChevronDown,
  ChevronUp,
  FlaskConical,
  EyeOff,
  Users,
  RefreshCw,
  X,
  Link2,
  Lock
} from 'lucide-react';
import { summaryReportsService, metricsTemplatesService, measureDefinitionsService, sectionMasterItemsService, jobReportsService, stocktakeSessionsService } from '../services';
import SearchableSelect from '../components/SearchableSelect';
import ManageMasterListDialog from '../components/ManageMasterListDialog';
import api from '../services/api';
import { formatMetricValue, formatVariance, getMetricColumnSuffix } from '../utils/metricFormatting';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import ReportPreviewContent from '../components/ReportPreviewContent';
import { StockAuditAttachmentPanel } from '../components/StockAuditAttachmentPanel';
import {
  ABBEY_COLORS, CURRENCY_SYMBOLS,
  formatNumber, formatPercent, formatCurrency,
  BEVERAGE_WIZARD_STEPS, FOOD_WIZARD_STEPS,
  getDefaultSections, MAX_HISTORY_ITEMS,
} from './report/reportConstants';
import {
  MiniTrendChart, KPITileWithChart,
  StocktakeResultTile, StocktakeResultTileWithTrend,
} from './report/ReportCharts';
import { getApiError } from '@/lib/utils';

// Constants, formatters, wizard steps, chart components imported from ./report/
// BrandedReportHeader is the canonical shared component at /components/BrandedReportHeader.jsx

// Merge a saved sections_json with the full default shape, the SAME way on
// every call site — used both when loading a report (to fill fields the
// Wizard needs that a freshly-linked/older report may not have saved yet)
// and as the dirty-check baseline. Using identical logic on both sides
// prevents a false "Unsaved Changes" flag (and the spurious autosave write
// it triggers) purely from default-key presence, which is not a real edit.
function mergeSectionsWithDefaults(sectionsJson) {
  if (!sectionsJson) return getDefaultSections();
  return {
    ...getDefaultSections(),
    ...sectionsJson,
    metrics: {
      ...getDefaultSections().metrics,
      ...(sectionsJson.metrics || {}),
    },
  };
}

// iter563 — Pre-fill Allowances/Management/Adjustment rows (descriptions only,
// amounts left blank) from the most recent published report for this site/scope.
async function fetchPriorSectionItemRows(siteId, scope, excludeReportId) {
  const empty = { allowances: [], management: [], adjustments: [] };
  if (!siteId || !scope) return empty;
  try {
    const prior = await summaryReportsService.getPriorSectionItems(siteId, scope, excludeReportId);
    return {
      allowances: (prior?.allowances || []).map(it => ({ description: it.description, at_retail: '' })),
      management: (prior?.management || []).map(it => ({ description: it.description, at_retail: '', at_cost: '' })),
      adjustments: (prior?.adjustments || []).map(it => ({ description: it.description, at_retail: '' })),
    };
  } catch {
    return empty;
  }
}

// 2026-02 — proper GBP sign placement: a negative value must render as
// "-£793.94", never the confusing "£-793.94" the browser's default
// toLocaleString-after-prefix pattern produces.
function fmtGbp(v) {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? '-' : ''}£${abs}`;
}


const SummaryStockAuditReportPage = () => {
  const { jobId, reportId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, canDirectlyPublishReports } = useAuth();
  const { settings } = useSettings();
  
  // Get currency symbol from settings (default to £)
  const currencySymbol = useMemo(() => {
    const region = settings?.default_region || 'UK';
    return CURRENCY_SYMBOLS[region] || '£';
  }, [settings?.default_region]);
  
  // Get scope from URL query params (for new report creation)
  const scopeFromUrl = searchParams.get('scope')?.toUpperCase();
  
  // Check if user can delete:
  // Admin/Senior Scheduler/Scheduler can delete any draft
  // Auditors can delete drafts they created
  const isAdminOrScheduler = user?.roles?.some(r => ['admin', 'senior_scheduler', 'scheduler'].includes(r.toLowerCase()));
  const isAuditorRole = user?.roles?.some(r => r.toLowerCase() === 'auditor');
  const canDelete = isAdminOrScheduler || isAuditorRole;
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autosaveFadingOut, setAutosaveFadingOut] = useState(false);
  const [recentEditNotice, setRecentEditNotice] = useState(null); // { name, secsAgo } | null
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [submittingForApproval, setSubmittingForApproval] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [schedulingNextAudit, setSchedulingNextAudit] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [report, setReport] = useState(null);
  // ── P1 Stocktake→Summary read-only prefill preview (D1–D8) ──────────
  // Loaded once after the report is fetched; never written to the wizard.
  // null = not yet fetched | false = no linked session | object = prefill data
  const [stocktakePrefill, setStocktakePrefill] = useState(null);
  const [prefillExpanded, setPrefillExpanded] = useState(false);
  // ── P3 Scope B — active link + mismatch status (read-only). ─────────
  // null = not yet fetched | false = not linked | object = active link info
  const [summaryLinkStatus, setSummaryLinkStatus] = useState(null);
  // Scope C — explicit, auditor-triggered refresh of a stale draft source
  const [refreshingSource, setRefreshingSource] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  // D1 — AP decision state (synced from report on load; saved explicitly via Save Decision btn)
  const [apDecision, setApDecision] = useState('');
  const [apDecisionReason, setApDecisionReason] = useState('');
  const [savingApDecision, setSavingApDecision] = useState(false);
  // D4 — Linked AP (loaded after report is published to show contextual link)
  const [linkedAp, setLinkedAp] = useState(null);
  const [linkedApLoading, setLinkedApLoading] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showUnpublishDialog, setShowUnpublishDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSaveDraftDialog, setShowSaveDraftDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState(null); // 'save' | 'save-exit'
  // Uploaded job reports — used for pre-publish upload gate
  const [jobReports, setJobReports] = useState([]);
  // AZ-native linked audits auto-generate the Stock Audit Report (no PS
  // File is required for these) — tracked here so the Publish pre-flight
  // gate reflects the real generation status instead of the legacy
  // manual-upload check, which does not apply to linked audits.
  const [linkedStockAttachment, setLinkedStockAttachment] = useState(null);
  // iter392 — silent autosave state for the wizard. Fires 3 s after the
  // user pauses editing and on every step transition so data is never
  // lost mid-session (e.g. during a deployment / tab close).
  const [autosaveState, setAutosaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const autosaveTimerRef = useRef(null);
  const handleSaveRef = useRef(null);
  const lastAutosaveAtRef = useRef(0);
  // iter556 — caps consecutive silent-autosave failures so a persistent
  // backend/network issue (e.g. a slow query, a transient DB blip) can't
  // turn into an infinite "Saving draft… / Autosave failed" loop every
  // 3s while the auditor is genuinely idle. Reset on any real edit or
  // on the next success, so autosave resumes normally once whatever
  // was wrong clears.
  const autosaveFailCountRef = useRef(0);
  const MAX_AUTOSAVE_RETRIES = 3;
  // Guards against a duplicate first-create POST caused by React
  // StrictMode's double-invocation of effects on mount (dev only), or any
  // other double-fire of loadReport() before the first create resolves.
  const creatingReportRef = useRef(false);
  // Bug fix (2026-09): guards the LOAD (GET-existing-report) path against
  // a stale/duplicate response overwriting fresher local state — e.g.
  // React StrictMode's dev-only double-invoke of this effect fires
  // loadReport() twice; if the 2nd (redundant) response resolves late
  // (slow network/server) it was overwriting whatever the user had
  // already typed in the few seconds since. Every loadReport() call
  // stamps a new generation; a response only gets applied if it's
  // still the most recent call by the time it resolves.
  const loadGenerationRef = useRef(0);

  // D1 — Sync local AP decision state whenever the report data changes
  // (on initial load and after any backend update that refreshes `report`).
  useEffect(() => {
    if (report) {
      setApDecision(report.ap_decision || '');
      setApDecisionReason(report.ap_decision_reason || '');
    }
  }, [report?.id, report?.ap_decision, report?.ap_decision_reason]);  // eslint-disable-line react-hooks/exhaustive-deps

  // D4 — Fetch the linked Action Plan once the report is published/approved with ap_decision='issue'
  useEffect(() => {
    const fetchLinkedAp = async () => {
      if (!report?.id || report?.ap_decision !== 'issue') {
        setLinkedAp(null);
        return;
      }
      setLinkedApLoading(true);
      try {
        const res = await import('../services/api').then(m => m.default.get(`/action-plans?report_id=${report.id}&limit=1`));
        const items = res.data?.items || res.data || [];
        setLinkedAp(Array.isArray(items) && items.length > 0 ? items[0] : null);
      } catch {
        setLinkedAp(null);
      } finally {
        setLinkedApLoading(false);
      }
    };
    fetchLinkedAp();
  }, [report?.id, report?.ap_decision, report?.status]);  // eslint-disable-line react-hooks/exhaustive-deps
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  
  // Metrics template state (for Beverage wizard)
  const [metricsTemplate, setMetricsTemplate] = useState(null);
  const [capturedMetrics, setCapturedMetrics] = useState([]);
  
  // Editable fields
  // 2026-04-28 — The date triple (audit_date, previous_audit_date, stock_period_days)
  // is held in local state. On first open they may be auto-populated from the
  // schedule / prior audit history; after that each field is independently
  // editable and all three persist verbatim to the DB on save. No cascade,
  // no self-heal — the user's entered values are sacred.
  const [stockPeriodDays, setStockPeriodDays] = useState(28);
  const [stockPeriodAutoPopulated, setStockPeriodAutoPopulated] = useState(false);
  const [editableAuditDate, setEditableAuditDate] = useState('');
  const [editablePreviousAuditDate, setEditablePreviousAuditDate] = useState('');
  const [sections, setSections] = useState(getDefaultSections());
  
  // History section collapsed state
  const [historyOpen, setHistoryOpen] = useState(false);
  // Track if history was auto-populated from prior audits
  const [historyAutoPopulated, setHistoryAutoPopulated] = useState(false);
  // YTD carry-forward state (iter — prior-ytd)
  // null = not yet loaded; { is_first_audit, prior_ytd, prior_audit_date, prior_report_id }
  const [priorYtdInfo, setPriorYtdInfo] = useState(null);
  
  // Determine wizard steps based on scope — inject client metrics step if template applies
  const isFoodScope = report?.scope === 'FOOD' || scopeFromUrl === 'FOOD';
  const isShopScope = report?.scope === 'SHOP' || scopeFromUrl === 'SHOP';
  const isFoodOrShopScope = isFoodScope || isShopScope;
  const hasMetricsStep = !isFoodScope && !isShopScope && metricsTemplate != null;
  
  const WIZARD_STEPS = React.useMemo(() => {
    if (isFoodScope || isShopScope) return FOOD_WIZARD_STEPS;
    if (!hasMetricsStep) return BEVERAGE_WIZARD_STEPS;
    // Inject metrics step after step 1 (Confirm Details), re-number
    const metricsStep = { id: 2, name: 'Report Metrics', icon: TrendingUp };
    return [
      BEVERAGE_WIZARD_STEPS[0], // Confirm Details = 1
      metricsStep, // Client Metrics = 2
      ...BEVERAGE_WIZARD_STEPS.slice(1).map((s, i) => ({ ...s, id: i + 3 }))
    ];
  }, [isFoodScope, isShopScope, hasMetricsStep, metricsTemplate]);
  
  // Dynamic measure definitions from central master list
  const [measureOptions, setMeasureOptions] = useState([]);

  // iter563 — Allowances/Management/Adjustment dropdown master lists
  const [allowanceOptions, setAllowanceOptions] = useState([]);
  const [managementOptions, setManagementOptions] = useState([]);
  const [adjustmentOptions, setAdjustmentOptions] = useState([]);
  // iter565 — "Manage list" pop-up (null when closed, else category string)
  const [manageListCategory, setManageListCategory] = useState(null);

  // Next Audit fields
  const [nextAuditTbc, setNextAuditTbc] = useState(false);
  const [nextAuditStartDate, setNextAuditStartDate] = useState('');
  const [nextAuditEndDate, setNextAuditEndDate] = useState('');

  // Check if user has entered any data into the report
  const hasAnyData = useCallback(() => {
    // Step 1 (period/audit dates) is entered before `sections` exists —
    // bug fix (2026-09): a fresh draft where the auditor only changed
    // the audit period never registered as "has data" (this only
    // checked `sections`), so autosave silently skipped saving it and
    // the Step-1 edit could be lost. Mirrors the same stored-vs-current
    // diff `hasUnsavedChanges` already uses below, so an untouched
    // draft (editable fields still equal to the stored report) is
    // still correctly reported as having no data.
    if (report) {
      const storedAudit = report.audit_date ? String(report.audit_date).split('T')[0] : '';
      const storedPrev = report.previous_audit_date ? String(report.previous_audit_date).split('T')[0] : '';
      if (stockPeriodDays !== report.stock_period_days) return true;
      if (editableAuditDate !== storedAudit) return true;
      if (editablePreviousAuditDate !== storedPrev) return true;
    }
    if (!sections) return false;
    const m = sections.metrics || {};
    // Check metric fields
    if (m.stockholding_value || m.stockholding_days || m.sales_for_period || 
        m.gp_percent || m.cos_percent || m.wastage_value || 
        m.stocktake_result_value || m.stocktake_result_percent ||
        m.exit_meeting_attended !== null) return true;
    // Check captured metrics (client template e.g. 4Cs)
    if (capturedMetrics.some(cm => cm.this_period_value != null || cm.last_period_value != null || cm.note)) return true;
    // Check history
    if (sections.history?.items?.length > 0) return true;
    // Check variance
    if (sections.variance?.shortages?.length > 0 || sections.variance?.surpluses?.length > 0) return true;
    // Check allowances
    if (sections.allowances?.length > 0) return true;
    // Check food allowances
    if (sections.food_allowances?.length > 0) return true;
    // Check management
    if (sections.management?.length > 0) return true;
    // Check adjustments
    if (sections.adjustments?.length > 0) return true;
    // Check abv_checks
    if (sections.abv_checks?.length > 0) return true;
    // Check observations
    const obs = sections.observations || {};
    if (obs.key || obs.root_causes || obs.actions) return true;
    return false;
  }, [sections, capturedMetrics, report, stockPeriodDays, editableAuditDate, editablePreviousAuditDate]);

  // Load report data
  const loadReport = useCallback(async () => {
    const myLoadGen = ++loadGenerationRef.current;
    setLoading(true);
    try {
      if (reportId) {
        // Load existing report by ID
        const data = await summaryReportsService.get(reportId);
        if (loadGenerationRef.current !== myLoadGen) return; // stale duplicate — a newer load has already taken over
        setReport(data);
        // Draft Recovery Notice (2026-09) — warn (don't block) if someone
        // ELSE saved this draft very recently, so an auditor opening a
        // shared draft doesn't unknowingly work from stale data.
        if (data.updated_by_user_id && user?.id && data.updated_by_user_id !== user.id && data.updated_at) {
          const secsAgo = Math.round((Date.now() - new Date(data.updated_at).getTime()) / 1000);
          if (secsAgo >= 0 && secsAgo < 120) {
            setRecentEditNotice({ name: data.updated_by_name || 'Another teammate', secsAgo });
          }
        }
        setStockPeriodDays(data.stock_period_days);
        setEditableAuditDate(data.audit_date ? String(data.audit_date).split('T')[0] : '');
        setEditablePreviousAuditDate(data.previous_audit_date ? String(data.previous_audit_date).split('T')[0] : '');
        // Load sections from sections_json or use defaults
        const loadedSections = mergeSectionsWithDefaults(data.sections_json);
        
        // Auto-populate history from prior KPIs if history is empty (for drafts)
        const hasHistory = loadedSections.history?.items?.length > 0;
        
        // Normalize: migrate legacy 'date' field to 'audit_date' for existing saved data
        if (hasHistory) {
          loadedSections.history.items = loadedSections.history.items.map(item => ({
            ...item,
            audit_date: item.audit_date || item.date || '',
          }));
        }
        
        // Check if existing history is incomplete (missing critical fields like
        // stocktake_result_percent or exit_meeting_attended). If so, re-fetch
        // from prior KPIs to get the complete data set.
        // iter558 — skip the chronologically EARLIEST item: it can never
        // have a previous period to compare against, so these fields are
        // legitimately always blank for it (not a bug). Previously this
        // was permanently true for any report whose history reaches back
        // to a site's first-ever period, re-fetching + overwriting the
        // (correctly healed) saved history on EVERY load.
        const sortedForCheck = hasHistory
          ? [...loadedSections.history.items].sort((a, b) => new Date(a.audit_date || 0) - new Date(b.audit_date || 0))
          : [];
        const historyIncomplete = hasHistory && data.status === 'DRAFT' && sortedForCheck.length > 1 &&
          sortedForCheck.slice(1).some(item =>
            !item.stocktake_result_percent && !item.exit_meeting_attended && item.exit_meeting_attended !== false
          );
        
        if ((!hasHistory || historyIncomplete) && data.site_id && data.scope) {
          try {
            const priorKpis = await summaryReportsService.getPriorKpis(data.site_id, data.scope, data.id);
            if (priorKpis && priorKpis.length > 0) {
              // priorKpis returned in chronological order (oldest first)
              const historyItems = priorKpis.map(kpi => ({
                audit_date: kpi.audit_date?.split('T')[0] || '',
                stockholding_value: kpi.stockholding_value || '',
                stockholding_days: kpi.stockholding_days || '',
                sales_for_period: kpi.sales_for_period || '',
                gp_percent: kpi.gp_percent || '',
                cos_percent: kpi.cos_percent || '',
                gp_before_allowances_percent: kpi.gp_before_allowances_percent || '',
                cos_before_allowances_percent: kpi.cos_before_allowances_percent || '',
                wastage_value: kpi.wastage_value || '',
                stocktake_result_value: kpi.stocktake_result_value || '',
                stocktake_result_percent: kpi.stocktake_result_percent || '',
                exit_meeting_attended: kpi.exit_meeting_attended,
                yield_percent: kpi.yield_percent || '',
              }));
              loadedSections.history = { items: historyItems };
              setHistoryAutoPopulated(true);
              setHistoryOpen(true);
              // Auto-prefill stock_period_days + previous_audit_date from the
              // most recent prior audit, but only for DRAFT reports where the
              // user hasn't yet set a previous_audit_date (sacred-values rule
              // — 2026-04-28). Once saved, the stored values are authoritative.
              const mostRecentPrior = priorKpis[priorKpis.length - 1];
              if (
                data.status === 'DRAFT' &&
                !data.previous_audit_date &&
                data.audit_date &&
                mostRecentPrior?.audit_date
              ) {
                const priorDate = mostRecentPrior.audit_date.split('T')[0];
                // UTC-anchored so DST transitions don't shift the count by 1
                const diff = Math.round(
                  (new Date(data.audit_date + 'T00:00:00Z').getTime() -
                    new Date(priorDate + 'T00:00:00Z').getTime()) / 86400000
                );
                if (diff >= 1 && diff <= 365) {
                  setStockPeriodDays(diff);
                  setEditablePreviousAuditDate(priorDate);
                  setStockPeriodAutoPopulated(true);
                  // Persist the auto-prefilled triple immediately so every
                  // downstream consumer (PDF, last step, server list view)
                  // reads the same dates as Step 1. Fire-and-forget; the
                  // next explicit save will retry if this fails.
                  (async () => {
                    try {
                      const updated = await summaryReportsService.update(data.id, {
                        stock_period_days: diff,
                        previous_audit_date: priorDate,
                      });
                      setReport(prev => prev ? {
                        ...prev,
                        stock_period_days: diff,
                        previous_audit_date: priorDate,
                        updated_at: updated?.updated_at || prev.updated_at,
                      } : prev);
                    } catch (_) {
                      /* best-effort; next explicit save will persist it */
                    }
                  })();
                }
              }
            }
          } catch { /* Prior KPI fetch failed — continue without it */ }
        } else if (hasHistory) {
          // History exists already — open by default so user can see it
          setHistoryOpen(true);
          setHistoryAutoPopulated(true);
        }

        // iter563 — Pre-fill Allowances/Management/Adjustment row descriptions
        // for a DRAFT report whose sections are still empty, from the site's
        // most recent published report of the same scope.
        const sectionsEmpty = !loadedSections.allowances?.length && !loadedSections.management?.length && !loadedSections.adjustments?.length;
        if (sectionsEmpty && data.status === 'DRAFT' && data.site_id && data.scope) {
          const priorRows = await fetchPriorSectionItemRows(data.site_id, data.scope, data.id);
          if (priorRows.allowances.length) loadedSections.allowances = priorRows.allowances;
          if (priorRows.management.length) loadedSections.management = priorRows.management;
          if (priorRows.adjustments.length) loadedSections.adjustments = priorRows.adjustments;
        }

        setSections(loadedSections);
        // Fetch prior YTD for carry-forward (first vs. later audit detection)
        if (data.site_id && data.scope) {
          try {
            const ytdInfo = await summaryReportsService.getPriorYtd(data.site_id, data.scope, data.id);
            setPriorYtdInfo(ytdInfo);
          } catch { /* best-effort; fallback: treat as first audit */ }
        }
        // Set next audit state
        setNextAuditTbc(data.next_audit_tbc || false);
        setNextAuditStartDate(data.next_audit_start_date || '');
        setNextAuditEndDate(data.next_audit_end_date || '');
      } else if (jobId) {
        // Check eligibility first
        const elig = await summaryReportsService.checkEligibility(jobId);
        setEligibility(elig);
        
        if (!elig.is_eligible) {
          toast.error(elig.reason || 'This job is not eligible for Summary Stock Audit Report');
          navigate(-1);
          return;
        }
        
        // Determine which scope to use
        let targetScope = scopeFromUrl;
        
        // Validate scope is eligible
        if (targetScope && !elig.eligible_scopes.includes(targetScope)) {
          toast.error(`Scope ${targetScope} is not eligible for this job`);
          navigate(-1);
          return;
        }
        
        // Default to first eligible scope if not specified
        if (!targetScope) {
          targetScope = elig.eligible_scopes[0];
        }
        
        // Check if report for this scope already exists
        const existingReport = elig.existing_reports?.find(r => r.scope === targetScope);
        
        if (existingReport) {
          // Navigate to existing report
          navigate(`/summary-reports/${existingReport.report_id}`, { replace: true });
          return;
        }
        
        // Create new report for this scope. Guard against a duplicate POST
        // if loadReport() fires twice before the first create resolves
        // (React StrictMode double-invokes effects on mount in dev).
        if (creatingReportRef.current) {
          return;
        }
        creatingReportRef.current = true;
        const data = await summaryReportsService.create(jobId, { scope: targetScope });
        setReport(data);
        setStockPeriodDays(data.stock_period_days);
        setEditableAuditDate(data.audit_date ? String(data.audit_date).split('T')[0] : '');
        setEditablePreviousAuditDate(data.previous_audit_date ? String(data.previous_audit_date).split('T')[0] : '');
        
        // Auto-populate history items with up to 4 prior KPIs (Phase 2)
        const defaultSections = getDefaultSections();
        try {
          if (data.site_id && targetScope) {
            const priorKpis = await summaryReportsService.getPriorKpis(data.site_id, targetScope, data.id);
            if (priorKpis && priorKpis.length > 0) {
              // priorKpis returned in chronological order (oldest first)
              const historyItems = priorKpis.map(kpi => ({
                audit_date: kpi.audit_date?.split('T')[0] || '',
                stockholding_value: kpi.stockholding_value || '',
                stockholding_days: kpi.stockholding_days || '',
                sales_for_period: kpi.sales_for_period || '',
                gp_percent: kpi.gp_percent || '',
                cos_percent: kpi.cos_percent || '',
                gp_before_allowances_percent: kpi.gp_before_allowances_percent || '',
                cos_before_allowances_percent: kpi.cos_before_allowances_percent || '',
                wastage_value: kpi.wastage_value || '',
                stocktake_result_value: kpi.stocktake_result_value || '',
                stocktake_result_percent: kpi.stocktake_result_percent || '',
                exit_meeting_attended: kpi.exit_meeting_attended,
                yield_percent: kpi.yield_percent || '',
              }));
              defaultSections.history = { items: historyItems };
              setHistoryAutoPopulated(true);
              setHistoryOpen(true);
              // Auto-prefill stock_period_days + previous_audit_date from the
              // most recent prior audit on brand-new reports (newly created,
              // no previous_audit_date stored yet). Per sacred-values rule
              // (2026-04-28), values are persisted immediately so they
              // round-trip verbatim on subsequent loads.
              const mostRecentPrior = priorKpis[priorKpis.length - 1];
              if (
                !data.previous_audit_date &&
                data.audit_date &&
                mostRecentPrior?.audit_date
              ) {
                const priorDate = mostRecentPrior.audit_date.split('T')[0];
                // UTC-anchored so DST transitions don't shift the count by 1
                const diff = Math.round(
                  (new Date(data.audit_date + 'T00:00:00Z').getTime() -
                    new Date(priorDate + 'T00:00:00Z').getTime()) / 86400000
                );
                if (diff >= 1 && diff <= 365) {
                  setStockPeriodDays(diff);
                  setEditablePreviousAuditDate(priorDate);
                  setStockPeriodAutoPopulated(true);
                  // Persist immediately so the PDF + last step of the wizard
                  // show the same dates as Step 1.
                  (async () => {
                    try {
                      const updated = await summaryReportsService.update(data.id, {
                        stock_period_days: diff,
                        previous_audit_date: priorDate,
                      });
                      setReport(prev => prev ? {
                        ...prev,
                        stock_period_days: diff,
                        previous_audit_date: priorDate,
                        updated_at: updated?.updated_at || prev.updated_at,
                      } : prev);
                    } catch (_) { /* best-effort; next save will persist it */ }
                  })();
                }
              }
            }
          }
        } catch { /* Prior KPI fetch failed — continue without it */ }

        // iter563 — Pre-fill Allowances/Management/Adjustment rows for a
        // brand-new report from the site's most recent published report.
        const priorRows = await fetchPriorSectionItemRows(data.site_id, targetScope, data.id);
        if (priorRows.allowances.length) defaultSections.allowances = priorRows.allowances;
        if (priorRows.management.length) defaultSections.management = priorRows.management;
        if (priorRows.adjustments.length) defaultSections.adjustments = priorRows.adjustments;

        setSections(defaultSections);
        // Fetch prior YTD for carry-forward (first vs. later audit detection)
        if (data.site_id && targetScope) {
          try {
            const ytdInfo = await summaryReportsService.getPriorYtd(data.site_id, targetScope, data.id);
            setPriorYtdInfo(ytdInfo);
          } catch { /* best-effort; fallback: treat as first audit */ }
        }
        toast.success(`${targetScope} Summary Report created`);
      }
    } catch (error) {
      console.error('Error loading report:', error);
      creatingReportRef.current = false;
      if (error.response?.status === 404) {
        toast.error('Report not found');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.detail || 'Cannot create report for this job');
      } else {
        toast.error('Failed to load report');
      }
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [jobId, reportId, navigate, scopeFromUrl]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Load job reports for upload pre-flight check.
  // Supports two routing patterns: /jobs/:jobId/... and /summary-reports/:reportId
  useEffect(() => {
    const resolvedJobId = jobId || report?.job_id;
    if (!resolvedJobId) return;
    jobReportsService.listForJob(resolvedJobId)
      .then(data => setJobReports(data?.reports || []))
      .catch(() => {}); // non-fatal — gate enforced server-side
  }, [jobId, report?.job_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // AZ-native linked audits: track the auto-generated Stock Audit Report
  // status so the Publish pre-flight gate (missingUploads below) reflects
  // reality instead of the legacy PS-File/manual-upload check, which does
  // not apply once a Summary is linked to a Stocktake source.
  const loadLinkedStockAttachment = useCallback(() => {
    if (!report?.id) return;
    summaryReportsService.getStockAuditAttachment(report.id)
      .then(data => setLinkedStockAttachment(data))
      .catch(() => {}); // non-fatal — gate enforced server-side
  }, [report?.id]);
  useEffect(() => {
    if (!report?.id || !summaryLinkStatus?.linked) { setLinkedStockAttachment(null); return; }
    loadLinkedStockAttachment();
  }, [report?.id, summaryLinkStatus?.linked, loadLinkedStockAttachment]);
  useEffect(() => {
    if (linkedStockAttachment?.status !== 'generating') return undefined;
    const t = setInterval(loadLinkedStockAttachment, 5000);
    return () => clearInterval(t);
  }, [linkedStockAttachment?.status, loadLinkedStockAttachment]);

  // ── P1 Stocktake→Summary prefill preview (D1–D8, read-only) ──────────
  // Fires once after an existing report is loaded. Returns 204 when no
  // matching locked session exists (silently hidden). Never writes wizard.
  useEffect(() => {
    if (!reportId) return; // new-creation path — no prefill to fetch
    let cancelled = false;
    api.get(`/summary-reports/${reportId}/stocktake-prefill`)
      .then(res => {
        if (!cancelled) setStocktakePrefill(res.status === 204 ? false : (res.data || false));
      })
      .catch(() => {
        if (!cancelled) setStocktakePrefill(false); // no banner on error
      });
    return () => { cancelled = true; };
  }, [reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── P3 Scope B — active link + mismatch status (read-only). ──────────
  // Fires once after an existing report is loaded. {linked:false} when
  // no active link (always true today — the Scope B tables don't exist
  // yet; see STOCKTAKE_SUMMARY_P2_CTO_PACKET.md). Never writes.
  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    summaryReportsService.getLinkStatus(reportId)
      .then(data => { if (!cancelled) setSummaryLinkStatus(data?.linked ? data : false); })
      .catch(() => { if (!cancelled) setSummaryLinkStatus(false); });
    return () => { cancelled = true; };
  }, [reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  // iter — YTD carry-forward: auto-compute running YTD from opening (prior audit)
  // + this period's figures whenever a period input changes.
  // Only active when:  report is editable (draft/reopen)  AND  not the first audit
  // Deps are the THREE period source fields, NOT the ytd_ fields, so this never loops.
  // 2026-02 correction — Honest-unavailable: a blank/missing prior or
  // period figure means UNRESOLVED (e.g. this session's own stock result
  // is still withheld — price data held), never a real £0. The previous
  // `|| 0` coalescing here silently produced a confident-looking YTD
  // Result even when the period's own result was withheld, masking it
  // exactly the way the backend's own YTD accumulation (ytd_service.py)
  // used to. Only auto-compute a YTD total when BOTH sides are genuinely
  // known numbers; otherwise leave that one field exactly as it already is.
  // Tracks the last value WE auto-set for the two editable YTD % fields —
  // lets the effect tell "still exactly what we last computed" (safe to
  // refresh) apart from "the auditor typed something different" (never
  // overwrite). Starts null, so a value already on the report when this
  // component mounts is treated as the auditor's own and left alone.
  const ytdAutoPctRef = useRef({ gp: null, result: null });
  useEffect(() => {
    if (!priorYtdInfo || !report?.is_editable) return;
    if (priorYtdInfo.is_first_audit) {
      // 2026-11 — opening YTD is unknown: either a genuine first audit, or
      // an 'unresolved' stale-coverage gap (e.g. the site's Customer Master
      // year-end was changed AFTER this draft's opening YTD was last
      // auto-computed and autosaved). The three amount fields below are
      // NEVER auditor-editable while `is_first_audit` is false (see the
      // `disabled={!canEdit || (priorYtdInfo && !priorYtdInfo.is_first_audit)}`
      // props on ytd-sales/ytd-gp-amount/ytd-result-amount) — so if one
      // still holds a value here, it is guaranteed to be a stale
      // system-computed figure left over from before, never something the
      // auditor typed. Clear it so the review banner isn't shown next to a
      // confident-looking stale total. The two YTD PERCENTAGE fields are
      // intentionally left untouched — the auditor CAN type into those at
      // any time, so a value found there cannot be assumed stale.
      setSections(prev => {
        const m = prev.metrics || {};
        if (!m.ytd_sales_amount && !m.ytd_gross_profit_amount && !m.ytd_result_amount) return prev;
        return { ...prev, metrics: { ...m, ytd_sales_amount: '', ytd_gross_profit_amount: '', ytd_result_amount: '' } };
      });
      return;
    }
    const prior = priorYtdInfo.prior_ytd || {};
    const m = sections.metrics || {};

    const parseKnown = (v) => {
      if (v === null || v === undefined || v === '') return null;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };

    const priorSales  = parseKnown(prior.ytd_sales_amount);
    const priorGp     = parseKnown(prior.ytd_gross_profit_amount);
    const priorResult = parseKnown(prior.ytd_result_amount);
    const periodSales  = parseKnown(m.sales_for_period);
    const periodGp     = parseKnown(m.gross_profit_amount);
    const periodResult = parseKnown(m.stocktake_result_value);

    const ytdSales  = (priorSales  !== null && periodSales  !== null) ? priorSales  + periodSales  : null;
    const ytdGp     = (priorGp     !== null && periodGp     !== null) ? priorGp     + periodGp     : null;
    const ytdResult = (priorResult !== null && periodResult !== null) ? priorResult + periodResult : null;
    const ytdGpPct  = (ytdSales !== null && ytdSales > 0 && ytdGp     !== null) ? (ytdGp     / ytdSales) * 100 : null;
    const ytdResPct = (ytdSales !== null && ytdSales > 0 && ytdResult !== null) ? (ytdResult  / ytdSales) * 100 : null;

    setSections(prev => {
      const curGpPct = prev.metrics?.ytd_gross_profit_percent || '';
      const curResultPct = prev.metrics?.ytd_result_percent || '';
      // Safe to refresh only when the field is still exactly what WE last
      // auto-set (or empty) — anything else means the auditor typed a
      // deliberate override, which must never be silently replaced.
      const gpIsOurs = curGpPct === '' || curGpPct === ytdAutoPctRef.current.gp;
      const resultIsOurs = curResultPct === '' || curResultPct === ytdAutoPctRef.current.result;
      const nextGpPct = ytdGpPct !== null ? ytdGpPct.toFixed(2) : '';
      const nextResultPct = ytdResPct !== null ? ytdResPct.toFixed(2) : '';
      ytdAutoPctRef.current = { gp: nextGpPct, result: nextResultPct };
      return {
        ...prev,
        metrics: {
          ...prev.metrics,
          // 2026-02 — these three amounts are fully system-computed (the
          // auditor cannot type into them), so the moment an input becomes
          // unknown the field MUST go blank, not keep whatever number was
          // last computed (that stale figure could be a pre-fix masked £0
          // persisted from before withheld inputs were preserved as `null`).
          ytd_sales_amount:          ytdSales  !== null ? ytdSales.toFixed(2)  : '',
          ytd_gross_profit_amount:   ytdGp     !== null ? ytdGp.toFixed(2)    : '',
          ytd_result_amount:         ytdResult !== null ? ytdResult.toFixed(2) : '',
          // Percentages CAN be manually overridden by the auditor — refresh
          // them only while they still match our own last auto-computed
          // figure; a genuine override is never touched.
          ytd_gross_profit_percent:  gpIsOurs ? nextGpPct : curGpPct,
          ytd_result_percent:        resultIsOurs ? nextResultPct : curResultPct,
        },
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    priorYtdInfo,
    report?.is_editable,
    sections.metrics?.sales_for_period,
    sections.metrics?.gross_profit_amount,
    sections.metrics?.stocktake_result_value,
  ]);
  useEffect(() => {
    const loadMeasures = async () => {
      try {
        const data = await measureDefinitionsService.list();
        setMeasureOptions((data || []).map(m => m.name));
      } catch (err) {
        console.error('Failed to load measure definitions:', err);
      }
    };
    loadMeasures();
  }, []);

  // iter563 — Fetch Allowances/Management/Adjustment master lists for dropdowns
  const loadSectionOptions = useCallback(async () => {
    try {
      const [a, m, adj] = await Promise.all([
        sectionMasterItemsService.list('allowance', true),
        sectionMasterItemsService.list('management', true),
        sectionMasterItemsService.list('adjustment', true),
      ]);
      setAllowanceOptions(a || []);
      setManagementOptions(m || []);
      setAdjustmentOptions(adj || []);
    } catch (err) {
      console.error('Failed to load section master items:', err);
    }
  }, []);

  useEffect(() => {
    loadSectionOptions();
  }, [loadSectionOptions]);

  // Resolve metrics template for Beverage scope and load captured metrics
  useEffect(() => {
    if (!report || report.scope !== 'BEVERAGE') return;
    
    const resolveTemplate = async () => {
      try {
        // Get site_id from report's job
        const siteId = report.site_id || report.job?.site_id;
        if (!siteId) return;
        
        const resolved = await metricsTemplatesService.resolve(siteId);
        if (resolved) {
          setMetricsTemplate(resolved);
          
          // Load existing captured metrics if report already has them
          if (report.id) {
            try {
              const existing = await metricsTemplatesService.getCapturedMetrics(report.id);
              if (existing && existing.length > 0) {
                setCapturedMetrics(existing);
                return;
              }
            } catch { /* no existing metrics */ }
          }
          
          // Initialize from template lines with prior values auto-populated
          const initialMetrics = resolved.lines.map(line => ({
            metric_label_snapshot: line.metric_label,
            metric_type_snapshot: line.metric_type || 'PERCENTAGE',
            display_order_snapshot: line.display_order,
            note_enabled_snapshot: line.note_enabled,
            this_period_value: null,
            last_period_value: resolved.prior_values[line.metric_label] ?? null,
            note: '',
          }));
          setCapturedMetrics(initialMetrics);
        }
      } catch (err) {
        console.error('Failed to resolve metrics template:', err);
      }
    };
    resolveTemplate();
  }, [report?.id, report?.scope, report?.site_id]);

  // Handle save (manual only — no autosave)
  const handleSave = async (silent = false) => {
    if (!report || !report.is_editable) return;
    
    // Check that at least one data point has been entered
    if (!hasAnyData()) {
      if (!silent) {
        toast.error('Please enter at least one data point before saving');
      }
      return;
    }
    
    setSaving(true);
    if (silent) setAutosaveState('saving');
    try {
      // Validate history items (max 4)
      const historyItems = sections.history?.items || [];
      if (historyItems.length > MAX_HISTORY_ITEMS) {
        toast.error(`Maximum ${MAX_HISTORY_ITEMS} history items allowed`);
        setSaving(false);
        if (silent) setAutosaveState('error');
        return;
      }
      
      const updated = await summaryReportsService.update(report.id, {
        stock_period_days: stockPeriodDays,
        audit_date: editableAuditDate || null,
        previous_audit_date: editablePreviousAuditDate || null,
        sections_json: sections
      });
      
      // Save captured metrics if template applies
      if (metricsTemplate && capturedMetrics.length > 0) {
        await metricsTemplatesService.saveCapturedMetrics(report.id, {
          template_id: metricsTemplate.template_id,
          template_title_snapshot: metricsTemplate.title,
          template_source: metricsTemplate.source,
          metrics: capturedMetrics,
        });
      }
      
      setReport(updated);
      setHasUnsavedChanges(false);
      if (silent) {
        autosaveFailCountRef.current = 0;
        setAutosaveState('saved');
        setAutosaveFadingOut(false);
        lastAutosaveAtRef.current = Date.now();
        // Brief "Saved ✓" confirmation, then fade out before dropping
        // back to idle once the badge has been seen (2026-09 polish).
        setTimeout(() => setAutosaveFadingOut(true), 1500);
        setTimeout(() => {
          setAutosaveState(prev => (prev === 'saved' ? 'idle' : prev));
          setAutosaveFadingOut(false);
        }, 2000);
      } else {
        toast.success('Draft saved successfully');
      }
    } catch (error) {
      console.error('Error saving report:', error);
      if (silent) {
        autosaveFailCountRef.current += 1;
        setAutosaveState('error');
      } else {
        toast.error(getApiError(error, 'Failed to save draft'));
      }
    } finally {
      setSaving(false);
    }
  };
  // iter392 — keep a ref to the latest handleSave so the autosave timer
  // and beforeunload handler always close over the current state.
  handleSaveRef.current = handleSave;

  // Prompt-based save: opens confirmation dialog before saving
  const promptSaveDraft = (action = 'save') => {
    if (!hasAnyData()) {
      toast.error('Please enter at least one data point before saving');
      return;
    }
    setPendingSaveAction(action);
    setShowSaveDraftDialog(true);
  };

  // Confirm save from the dialog
  const confirmSaveDraft = async () => {
    setShowSaveDraftDialog(false);
    if (pendingSaveAction === 'save-exit') {
      await doSaveAndExit();
    } else if (pendingSaveAction === 'return-to-stocktake') {
      await doSaveAndExit(`/stocktaking/sessions/${summaryLinkStatus?.session_id}`);
    } else {
      await handleSave(false);
    }
    setPendingSaveAction(null);
  };

  // iter392 — Step change now auto-saves the draft (silent) if there
  // are unsaved changes. Failing silently is fine because the user can
  // still hit "Save & Exit" / "Save Draft" — the autosave is a safety
  // net so partially-entered audit data is never lost when the auditor
  // jumps between wizard steps (e.g. just before a deploy).
  const handleStepChange = (newStep) => {
    if (hasUnsavedChanges && report?.is_editable && hasAnyData() && !saving) {
      // Cancel any pending debounced autosave so we don't race.
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      // Fire-and-forget — never block the UI on the network round-trip.
      handleSaveRef.current?.(true);
    }
    setCurrentStep(newStep);
  };

  // iter392 — Debounced silent autosave. Fires 3 s after the auditor
  // pauses editing. Intentionally cheap: skips when not editable, when
  // nothing has been entered yet, or when a manual save / publish /
  // delete is already in flight. This is what prevents mid-session
  // data loss reported during a production deploy.
  useEffect(() => {
    // No work to do unless the user has dirtied the draft.
    if (!hasUnsavedChanges) return;
    if (!report?.is_editable) return;
    if (saving || publishing || unpublishing || deleting || submittingForApproval) return;
    if (!hasAnyData()) return;
    // iter556 — stop retrying after MAX_AUTOSAVE_RETRIES consecutive
    // failures instead of hammering the backend every 3s forever; the
    // "Autosave failed — please save manually" banner stays up (it's
    // driven by autosaveState, not by this timer) until the user saves
    // manually or makes a fresh edit (see the reset effect below).
    if (autosaveFailCountRef.current >= MAX_AUTOSAVE_RETRIES) return;

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      handleSaveRef.current?.(true);
    }, 3000);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [
    hasUnsavedChanges,
    report?.is_editable,
    saving,
    publishing,
    unpublishing,
    deleting,
    submittingForApproval,
    // Reset the 3 s window whenever the underlying data changes so the
    // last keystroke wins (debounce, not throttle).
    sections,
    stockPeriodDays,
    editableAuditDate,
    editablePreviousAuditDate,
    capturedMetrics,
    hasAnyData,
  ]);

  // iter392 — Best-effort save on tab/window close while dirty. We do
  // NOT use sendBeacon for the JSON payload (the API requires a JSON
  // Content-Type + auth header). Instead we fire the standard save and
  // prompt the user to wait via the browser's beforeunload dialog,
  // which gives the in-flight request a chance to land.
  useEffect(() => {
    const handler = (e) => {
      if (!hasUnsavedChanges) return;
      if (!report?.is_editable) return;
      // Fire-and-forget; the browser will keep the connection open
      // long enough for small payloads.
      try { handleSaveRef.current?.(true); } catch { /* ignore */ }
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges, report?.is_editable]);

  // Scope C — the Summary publish/submit/approve endpoints return 409
  // {reason:'source_stocktake_not_published'} when this report's source
  // Stocktake session is only 'completed' (draft-stage). Detected here so
  // the UI can offer a single confirm-first-publish-Stocktake step instead
  // of a dead-end error — never auto-published without this explicit ask.
  const [showPublishStocktakeFirstDialog, setShowPublishStocktakeFirstDialog] = useState(false);
  const [pendingStocktakeGate, setPendingStocktakeGate] = useState(null); // {session_id, session_reference, retry}
  const [publishingStocktakeFirst, setPublishingStocktakeFirst] = useState(false);

  const isSourceStocktakeGateError = (error) => error?.response?.status === 409
    && error?.response?.data?.detail?.reason === 'source_stocktake_not_published';

  const confirmPublishStocktakeFirst = async () => {
    if (!pendingStocktakeGate) return;
    setPublishingStocktakeFirst(true);
    try {
      await stocktakeSessionsService.changeStatus(pendingStocktakeGate.session_id, 'locked');
      setShowPublishStocktakeFirstDialog(false);
      toast.success(`Stocktake ${pendingStocktakeGate.session_reference || ''} published. Continuing…`);
      await pendingStocktakeGate.retry();
    } catch (error) {
      // Stocktake's own completeness/permission gates failed — the
      // Summary draft is left exactly as it was, nothing published.
      toast.error(getApiError(error, 'Could not publish the source Stocktake — the Summary remains a draft.'));
    } finally {
      setPublishingStocktakeFirst(false);
      setPendingStocktakeGate(null);
    }
  };

  // Scope C — explicit refresh of a stale draft-stage source. Never
  // automatic, never triggered by a GET. Re-fetches link-status after so
  // the banner reflects the new (non-stale) state immediately.
  const handleRefreshSource = async () => {
    if (!report) return;
    setRefreshingSource(true);
    try {
      const result = await summaryReportsService.refreshSource(report.id);
      setReport(result.report);
      setSections(mergeSectionsWithDefaults(result.report.sections_json));
      const status = await summaryReportsService.getLinkStatus(report.id);
      setSummaryLinkStatus(status?.linked ? status : false);
      toast.success(`Source refreshed to v${result.new_version}. Locked fields updated from the latest calculation.`);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to refresh source'));
    } finally {
      setRefreshingSource(false);
    }
  };

  // Handle publish
  const handlePublish = async () => {
    if (!report) return;
    
    setPublishing(true);
    try {
      // Validate food allowances have value_type selected
      if (isFoodOrShopScope && sections.food_allowances?.length > 0) {
        const missingType = sections.food_allowances.some(a => !a.value_type);
        if (missingType) {
          toast.error('All food allowances must have Retail or Cost selected before publishing');
          setPublishing(false);
          return;
        }
      }
      
      // Save first if there are unsaved changes
      if (hasUnsavedChanges) {
        await summaryReportsService.update(report.id, {
          stock_period_days: stockPeriodDays,
          audit_date: editableAuditDate || null,
          previous_audit_date: editablePreviousAuditDate || null,
          sections_json: sections
        });
        
        // Save captured metrics if template applies
        if (metricsTemplate && capturedMetrics.length > 0) {
          await metricsTemplatesService.saveCapturedMetrics(report.id, {
            template_id: metricsTemplate.template_id,
            template_title_snapshot: metricsTemplate.title,
            template_source: metricsTemplate.source,
            metrics: capturedMetrics,
          });
        }
      }
      
      // Then publish
      const published = await summaryReportsService.publish(report.id);
      setReport(published);
      setHasUnsavedChanges(false);
      setShowPublishDialog(false);
      toast.success('Summary Stock Audit Report published successfully. It is now available in the repository.');
    } catch (error) {
      if (isSourceStocktakeGateError(error)) {
        setShowPublishDialog(false);
        setPendingStocktakeGate({ ...error.response.data.detail, retry: handlePublish });
        setShowPublishStocktakeFirstDialog(true);
        return;
      }
      console.error('Error publishing report:', error);
      toast.error(getApiError(error, 'Failed to publish report'));
    } finally {
      setPublishing(false);
    }
  };

  // Approval workflow detection
  const approvalMode = settings?.summary_reports_publish_mode === 'approval_required';
  // iter416 — Trusted auditors (flag `can_directly_publish_reports`) bypass
  // approval just like Admin/Scheduler — matches backend iter404. Without
  // this, Sophie Gardner saw only "Submit for Approval" despite the flag.
  const isAuditorOnlyRole = isAuditorRole && !isAdminOrScheduler && !canDirectlyPublishReports;
  const isPendingApproval = report?.status === 'PENDING_APPROVAL';
  const showSubmitForApproval = approvalMode && isAuditorOnlyRole && report?.status === 'DRAFT';
  const showApproveButton = (isAdminOrScheduler || canDirectlyPublishReports) && isPendingApproval;

  // Upload pre-flight: compute which required files are missing for the current scope.
  // AZ-native linked audits (summaryLinkStatus.linked) never require a manual
  // PS File — the Stock Audit Report is generated automatically from the
  // linked Stocktake source (see StockAuditAttachmentPanel). This branch
  // mirrors the backend's `linked_stock_report_service.missing_for_publish`
  // so the UI gate and the server's publish gate never disagree.
  const missingUploads = (() => {
    if (!report) return [];
    if (summaryLinkStatus?.linked) {
      const blocking = [];
      // Mirrors backend `_check_source_stocktake_published_gate` (409
      // 'source_stocktake_not_published'): a Summary linked to a Stocktake
      // that is still 'completed' (not yet locked/published) cannot be
      // published. Surface this BEFORE the click, not only after a 409.
      if (summaryLinkStatus.source_status && summaryLinkStatus.source_status !== 'locked') {
        blocking.push(
          `Source Stocktake ${summaryLinkStatus.session_reference ? `#${summaryLinkStatus.session_reference}` : ''} is not published yet — publish (lock) it first`
        );
      }
      const status = linkedStockAttachment?.status;
      if (status === 'source_stale') blocking.push('Stock Audit Report (Beverage) — the Stocktake calculation changed; refresh the source, then regenerate');
      else if (status === 'failed') blocking.push(`Stock Audit Report (Beverage) — generation failed (${linkedStockAttachment?.error || 'unknown error'}); retry generation`);
      else if (status === 'generating') blocking.push('Stock Audit Report (Beverage) — generation still in progress');
      else if (status !== 'ready') blocking.push('Stock Audit Report (Beverage) — generate it from the Stocktake source');
      return blocking;
    }
    const scope = report.scope?.toUpperCase();
    const requiredService = scope === 'BEVERAGE' ? 'beverage' : (scope === 'FOOD' || scope === 'SHOP') ? 'food' : null;
    if (!requiredService) return [];
    const uploaded = new Set(
      jobReports
        .filter(r => r.report_type === 'ps' || r.report_type === 'stock')
        .map(r => `${r.report_type}:${r.service_type}`)
    );
    const missing = [];
    if (!uploaded.has(`ps:${requiredService}`)) missing.push(`PS File (${requiredService.charAt(0).toUpperCase() + requiredService.slice(1)})`);
    if (!uploaded.has(`stock:${requiredService}`)) missing.push(`Stock Audit Report (${requiredService.charAt(0).toUpperCase() + requiredService.slice(1)})`);
    return missing;
  })();

  // Handle submit for approval
  const handleSubmitForApproval = async () => {
    if (!report) return;
    // Pre-flight: block if required uploads are missing
    if (missingUploads.length > 0) {
      toast.error(`Cannot submit: please upload the following files first: ${missingUploads.join(', ')}`);
      return;
    }
    setSubmittingForApproval(true);
    try {
      // Save first if there are unsaved changes
      if (hasUnsavedChanges) {
        await summaryReportsService.update(report.id, {
          stock_period_days: stockPeriodDays,
          audit_date: editableAuditDate || null,
          previous_audit_date: editablePreviousAuditDate || null,
          sections_json: sections
        });
      }
      const res = await api.post(`/summary-reports/${report.id}/submit-for-approval`);
      setReport(res.data);
      setHasUnsavedChanges(false);
      toast.success('Report submitted for approval. Schedulers will be notified.');
    } catch (error) {
      if (isSourceStocktakeGateError(error)) {
        setPendingStocktakeGate({ ...error.response.data.detail, retry: handleSubmitForApproval });
        setShowPublishStocktakeFirstDialog(true);
        return;
      }
      toast.error(getApiError(error, 'Failed to submit for approval'));
    } finally {
      setSubmittingForApproval(false);
    }
  };

  // Handle approve
  const handleApprove = async () => {
    if (!report) return;
    setPublishing(true);
    try {
      // Save first if there are unsaved changes (scheduler may have edited)
      if (hasUnsavedChanges) {
        await summaryReportsService.update(report.id, {
          stock_period_days: stockPeriodDays,
          audit_date: editableAuditDate || null,
          previous_audit_date: editablePreviousAuditDate || null,
          sections_json: sections
        });
      }
      const res = await api.post(`/summary-reports/${report.id}/approve`);
      setReport(res.data);
      setHasUnsavedChanges(false);
      toast.success('Report approved and published successfully.');
    } catch (error) {
      if (isSourceStocktakeGateError(error)) {
        setPendingStocktakeGate({ ...error.response.data.detail, retry: handleApprove });
        setShowPublishStocktakeFirstDialog(true);
        return;
      }
      toast.error(getApiError(error, 'Failed to approve report'));
    } finally {
      setPublishing(false);
    }
  };

  // Handle return to auditor
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnComments, setReturnComments] = useState('');
  const [returning, setReturning] = useState(false);

  // D1 — Save AP decision (explicit save, not autosave)
  const handleSaveApDecision = async () => {
    if (!report) return;
    setSavingApDecision(true);
    try {
      const payload = {
        ap_decision: apDecision || null,
        ap_decision_reason: apDecisionReason || null,
      };
      const res = await summaryReportsService.update(report.id, payload);
      setReport(res);
      toast.success(
        apDecision === 'issue'
          ? 'AP Decision saved — an Action Plan will be created on publication.'
          : apDecision === 'suppress'
          ? 'AP Decision saved — no Action Plan will be created.'
          : 'AP Decision cleared.'
      );
    } catch (error) {
      toast.error('Failed to save AP decision. Please try again.');
    } finally {
      setSavingApDecision(false);
    }
  };

  const handleReturnToAuditor = async () => {
    if (!report) return;
    setReturning(true);
    try {
      const res = await api.post(`/summary-reports/${report.id}/return-to-auditor`, {
        comments: returnComments || null,
      });
      setReport(res.data);
      setShowReturnDialog(false);
      setReturnComments('');
      toast.success('Report returned to auditor for further editing.');
    } catch (error) {
      toast.error(getApiError(error, 'Failed to return report'));
    } finally {
      setReturning(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!report) return;
    
    setDeleting(true);
    try {
      await summaryReportsService.delete(report.id);
      toast.success('Report deleted successfully');
      setShowDeleteDialog(false);
      navigate(-1);
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error(getApiError(error, 'Failed to delete report'));
    } finally {
      setDeleting(false);
    }
  };

  // Handle unpublish
  const handleUnpublish = async () => {
    if (!report) return;
    
    setUnpublishing(true);
    try {
      const updated = await summaryReportsService.unpublish(report.id);
      setReport(updated);
      setShowUnpublishDialog(false);
      toast.success('Report unpublished — reverted to Draft. You can now edit or delete it.');
    } catch (error) {
      console.error('Error unpublishing report:', error);
      toast.error(getApiError(error, 'Failed to unpublish report'));
    } finally {
      setUnpublishing(false);
    }
  };

  // Handle export PDF
  const handleExportPdf = async () => {
    if (!report) return;
    
    setExportingPdf(true);
    try {
      await summaryReportsService.exportPdf(report.id);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(getApiError(error, 'Failed to export PDF'));
    } finally {
      setExportingPdf(false);
    }
  };

  // Internal save & exit (called after user confirms via dialog)
  const doSaveAndExit = async (destination = '/reports-repository') => {
    if (!report) return;
    
    setSaving(true);
    try {
      await summaryReportsService.update(report.id, {
        stock_period_days: stockPeriodDays,
        audit_date: editableAuditDate || null,
        previous_audit_date: editablePreviousAuditDate || null,
        sections_json: sections
      });
      
      // Save captured metrics if template applies
      if (metricsTemplate && capturedMetrics.length > 0) {
        await metricsTemplatesService.saveCapturedMetrics(report.id, {
          template_id: metricsTemplate.template_id,
          template_title_snapshot: metricsTemplate.title,
          template_source: metricsTemplate.source,
          metrics: capturedMetrics,
        });
      }
      
      setHasUnsavedChanges(false);
      toast.success('Draft saved successfully');
      navigate(destination);
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(getApiError(error, 'Failed to save draft'));
    } finally {
      setSaving(false);
    }
  };

  // Handle Save & Exit button click — if no data entered, just exit (discard empty draft)
  const handleSaveAndExit = async () => {
    if (!hasAnyData()) {
      // No data entered — delete the empty draft and navigate away
      if (report && report.is_editable) {
        try { await summaryReportsService.delete(report.id); } catch (_) {}
      }
      navigate(-1);
      return;
    }
    promptSaveDraft('save-exit');
  };

  // Return to Stocktake — navigates back to the linked source session.
  // Unsaved edits get the same Save Draft/Stay choice as every other exit
  // path; a clean report navigates straight across.
  const handleReturnToStocktake = () => {
    if (!summaryLinkStatus?.linked || !summaryLinkStatus.session_id) return;
    const destination = `/stocktaking/sessions/${summaryLinkStatus.session_id}`;
    if (!hasUnsavedChanges) {
      navigate(destination);
      return;
    }
    setPendingSaveAction('return-to-stocktake');
    setShowSaveDraftDialog(true);
  };

  // Handle schedule next audit
  const handleScheduleNextAudit = async () => {
    if (!report) return;
    
    if (!nextAuditTbc && !nextAuditStartDate) {
      toast.error('Please provide a start date or check "Next date to be agreed"');
      return;
    }
    
    setSchedulingNextAudit(true);
    try {
      const result = await summaryReportsService.scheduleNextAudit(report.id, {
        tbc: nextAuditTbc,
        start_date: nextAuditTbc ? null : nextAuditStartDate,
        end_date: nextAuditTbc ? null : (nextAuditEndDate || null)
      });
      
      setReport(prev => ({
        ...prev,
        next_audit_tbc: result.next_audit_tbc,
        next_audit_start_date: result.next_audit_start_date,
        next_audit_end_date: result.next_audit_end_date,
        next_audit_job_id: result.next_audit_job_id
      }));
      
      if (result.next_audit_tbc) {
        toast.success('Next audit marked as "to be confirmed"');
      } else {
        toast.success(`Next ${report.scope} audit scheduled (Job #${result.next_audit_job_id})`);
      }
    } catch (error) {
      console.error('Error scheduling next audit:', error);
      toast.error(getApiError(error, 'Failed to schedule next audit'));
    } finally {
      setSchedulingNextAudit(false);
    }
  };

  // Track changes
  useEffect(() => {
    if (report) {
      const originalSections = mergeSectionsWithDefaults(report.sections_json);
      const storedAudit = report.audit_date ? String(report.audit_date).split('T')[0] : '';
      const storedPrev = report.previous_audit_date ? String(report.previous_audit_date).split('T')[0] : '';
      const hasChanges = 
        stockPeriodDays !== report.stock_period_days ||
        editableAuditDate !== storedAudit ||
        editablePreviousAuditDate !== storedPrev ||
        JSON.stringify(sections) !== JSON.stringify(originalSections);
      setHasUnsavedChanges(hasChanges);
    }
  }, [stockPeriodDays, editableAuditDate, editablePreviousAuditDate, sections, report]);

  // iter556 — a genuine new edit (not the save-response round-trip
  // above) means whatever caused the last autosave failure may no
  // longer apply — give it a fresh set of retries.
  useEffect(() => {
    autosaveFailCountRef.current = 0;
  }, [sections, stockPeriodDays, editableAuditDate, editablePreviousAuditDate]);

  // Format date helper
  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }, []);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get scope display info
  const getScopeInfo = (scope) => {
    if (scope === 'FOOD') {
      return {
        label: 'Food',
        icon: <UtensilsCrossed className="h-5 w-5" />,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50'
      };
    }
    if (scope === 'SHOP') {
      return {
        label: 'Shop',
        icon: <UtensilsCrossed className="h-5 w-5" />,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50'
      };
    }
    return {
      label: 'Beverage',
      icon: <Wine className="h-5 w-5" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    };
  };

  // 2026-11 — explicit, reviewer-triggered recalculation of the opening
  // YTD amount fields (never the percentages — see service/backend docs).
  const [recalculatingYtd, setRecalculatingYtd] = useState(false);
  const handleRecalculateOpeningYtd = async () => {
    if (!reportId) return;
    setRecalculatingYtd(true);
    try {
      const res = await summaryReportsService.recalculateOpeningYtd(reportId);
      if (res.changed) {
        setSections(prev => ({
          ...prev,
          metrics: {
            ...prev.metrics,
            ytd_sales_amount: res.after.ytd_sales_amount || '',
            ytd_gross_profit_amount: res.after.ytd_gross_profit_amount || '',
            ytd_result_amount: res.after.ytd_result_amount || '',
          },
        }));
        toast.success('Opening YTD recalculated and saved', {
          description: `Sales ${res.before.ytd_sales_amount || '—'} → ${res.after.ytd_sales_amount || '(blank — needs review)'}`,
        });
      } else {
        toast.info('Opening YTD already matches the current financial-year basis — nothing to change');
      }
      // Re-fetch so the banner/lock state reflects the freshly-saved report.
      const refreshed = await summaryReportsService.get(reportId);
      setReport(refreshed);
    } catch (err) {
      toast.error(getApiError(err, 'Could not recalculate opening YTD'));
    } finally {
      setRecalculatingYtd(false);
    }
  };

  // Update section helper - uses functional update to avoid closure issues
  const updateSection = useCallback((sectionKey, value) => {
    setSections(prev => ({
      ...prev,
      [sectionKey]: value
    }));
  }, []);

  // Calculate allowances subtotal
  const allowancesSubtotal = useMemo(() => {
    const allowances = sections.allowances || [];
    return {
      retail: allowances.reduce((sum, item) => sum + (parseFloat(item.at_retail) || 0), 0),
      cost: allowances.reduce((sum, item) => sum + (parseFloat(item.at_cost) || 0), 0)
    };
  }, [sections.allowances]);

  // Calculate management subtotal
  const managementSubtotal = useMemo(() => {
    const management = sections.management || [];
    return {
      retail: management.reduce((sum, item) => sum + (parseFloat(item.at_retail) || 0), 0),
      cost: management.reduce((sum, item) => sum + (parseFloat(item.at_cost) || 0), 0)
    };
  }, [sections.management]);

  // Calculate adjustments subtotal
  const adjustmentsSubtotal = useMemo(() => {
    const adjustments = sections.adjustments || [];
    return {
      retail: adjustments.reduce((sum, item) => sum + (parseFloat(item.at_retail) || 0), 0),
      cost: 0
    };
  }, [sections.adjustments]);

  // Prepare trend chart data (combine history + current)
  const trendChartData = useMemo(() => {
    const metrics = sections.metrics || {};
    const historyItems = sections.history?.items || [];
    
    // Create data points from history
    const dataPoints = historyItems
      .filter(item => item.audit_date) // Only include items with dates
      .map(item => ({
        date: formatDate(item.audit_date),
        sortDate: new Date(item.audit_date).getTime(),
        gp_percent: parseFloat(item.gp_percent) || 0,
        gp_before_allowances_percent: parseFloat(item.gp_before_allowances_percent) || 0,
        stockholding_value: parseFloat(item.stockholding_value) || 0,
        stockholding_days: parseFloat(item.stockholding_days) || 0,
        sales_for_period: parseFloat(item.sales_for_period) || 0,
        cos_percent: parseFloat(item.cos_percent) || 0,
        wastage_value: parseFloat(item.wastage_value) || 0,
        stocktake_result_value: parseFloat(item.stocktake_result_value) || 0,
        stocktake_result_percent: parseFloat(item.stocktake_result_percent) || 0,
        exit_meeting_attended: item.exit_meeting_attended
      }));
    
    // Add current data point if we have current metrics and report audit_date
    if (report?.audit_date && (metrics.gp_percent || metrics.stockholding_value || metrics.wastage_value || metrics.stocktake_result_value)) {
      dataPoints.push({
        date: formatDate(report.audit_date),
        sortDate: new Date(report.audit_date).getTime(),
        gp_percent: parseFloat(metrics.gp_percent) || 0,
        gp_before_allowances_percent: parseFloat(metrics.gp_before_allowances_percent) || 0,
        stockholding_value: parseFloat(metrics.stockholding_value) || 0,
        stockholding_days: parseFloat(metrics.stockholding_days) || 0,
        sales_for_period: parseFloat(metrics.sales_for_period) || 0,
        cos_percent: parseFloat(metrics.cos_percent) || 0,
        wastage_value: parseFloat(metrics.wastage_value) || 0,
        stocktake_result_value: parseFloat(metrics.stocktake_result_value) || 0,
        stocktake_result_percent: parseFloat(metrics.stocktake_result_percent) || 0,
        exit_meeting_attended: metrics.exit_meeting_attended,
        isCurrent: true
      });
    }
    
    // Sort by date (oldest first)
    dataPoints.sort((a, b) => a.sortDate - b.sortDate);
    
    return dataPoints;
  }, [sections.metrics, sections.history, report?.audit_date, formatDate]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Report not found or failed to load.</p>
        <Button variant="outline" className="mt-4" onClick={() => window.history.back()}>Go Back</Button>
      </div>
    );
  }

  const isPublished = report.status === 'PUBLISHED';
  const canEdit = report.is_editable;
  const hasNextAuditScheduled = report.next_audit_job_id || report.next_audit_tbc;
  const scopeInfo = getScopeInfo(report.scope);
  // Source-backed fields locked by an active Stocktake link (D1-D8) —
  // server-enforced (PUT rejects a changed value); this drives the
  // matching visible read-only state + explanation in the Wizard.
  const lockedFieldPaths = summaryLinkStatus?.locked_fields || [];
  const isFieldLocked = (path) => lockedFieldPaths.includes(path);
  const lockedFieldHint = summaryLinkStatus?.session_reference
    ? `Locked from Stocktake ${summaryLinkStatus.session_reference} — edit the source session and relaunch to change this value.`
    : 'Locked from the linked Stocktake session — edit the source session and relaunch to change this value.';

  // =============================================
  // WIZARD STEP CONTENT - Rendered inline to fix input focus issues
  // =============================================

  // Map Food wizard steps to content. Food steps:
  // 1=Confirm, 2=KPIs, 3=Food Allowances, 4=Observations, 5=Review
  const getFoodStepKey = (step) => {
    const map = { 1: 'confirm', 2: 'kpi', 3: 'food_allowances', 4: 'observations', 5: 'review' };
    return map[step] || 'confirm';
  };
  // Map Beverage wizard steps to content. Beverage steps:
  // Without template: 1=Confirm, 2=KPIs, 3=PLU Variance, 4=Allowances & Management, 5=ABV, 6=Observations, 7=Review
  // With template: 1=Confirm, 2=ClientMetrics, 3=KPIs, 4=PLU Variance, 5=Allowances & Management, 6=ABV, 7=Observations, 8=Review
  const getBeverageStepKey = (step) => {
    if (hasMetricsStep) {
      const map = { 1: 'confirm', 2: 'client_metrics', 3: 'kpi', 4: 'variance', 5: 'allowances', 6: 'abv', 7: 'observations', 8: 'review' };
      return map[step] || 'confirm';
    }
    const map = { 1: 'confirm', 2: 'kpi', 3: 'variance', 4: 'allowances', 5: 'abv', 6: 'observations', 7: 'review' };
    return map[step] || 'confirm';
  };

  const getStepKey = isFoodOrShopScope ? getFoodStepKey : getBeverageStepKey;

  const renderStepContent = () => {
    const stepKey = getStepKey(currentStep);
    switch (stepKey) {
      case 'confirm':
        // Step 1: Confirm Details
        // Confirm Details provenance fix (2026-02, corrected 2026-02): audit_date /
        // previous_audit_date are locked server-side once a genuine source
        // exists (see stocktake_summary_link_service._compute_locked_date_fields).
        // stock_period_days is deliberately NOT part of that lock set — the
        // calculated value is only the visible default; the auditor may
        // enter a validated override (server no longer rejects it, even on
        // a report whose persisted locked_report_fields predates this fix —
        // see find_locked_report_field_violations).
        // `lockedReportFields` mirrors the server's locking decision for the
        // two date fields — the UI never decides locking on its own.
        const lockedReportFields = report.sections_json?.source_prefill?.locked_report_fields || {};
        const isAuditDateLocked = 'audit_date' in lockedReportFields;
        const isPreviousAuditLocked = 'previous_audit_date' in lockedReportFields;
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {report.parent_client_id && (
              <div>
                <Label className="text-sm text-muted-foreground">Client</Label>
                <p className="mt-1 font-medium text-lg" data-testid="step1-client">{report.client_name}</p>
              </div>
              )}
              <div>
                <Label className="text-sm text-muted-foreground">Site</Label>
                <p className="mt-1 font-medium text-lg" data-testid="step1-site">{report.site_name}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground flex items-center gap-1.5">
                  Audit Date
                  {isAuditDateLocked && (
                    <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500" title="Sourced from the linked Stocktake's scheduled audit date — cannot be edited">
                      <Lock className="h-3 w-3" /> From Stocktake
                    </span>
                  )}
                </Label>
                {canEdit && !isAuditDateLocked ? (
                  <Input
                    type="date"
                    value={editableAuditDate}
                    onChange={(e) => {
                      setEditableAuditDate(e.target.value);
                      setStockPeriodAutoPopulated(false);
                    }}
                    className="mt-1 w-48 text-lg font-medium"
                    data-testid="step1-audit-date-input"
                  />
                ) : (
                  <p
                    className={`mt-1 font-medium text-lg ${isAuditDateLocked ? 'text-slate-500' : ''}`}
                    data-testid="step1-audit-date"
                  >
                    {formatDate(editableAuditDate || report.audit_date)}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Scope</Label>
                <p className="mt-1 font-medium text-lg flex items-center gap-2" data-testid="step1-scope">
                  <span className={scopeInfo.color}>{scopeInfo.icon}</span>
                  {scopeInfo.label}
                </p>
              </div>
            </div>
            
            <div className="border-t pt-6">
              <Label className="text-sm font-medium flex items-center gap-1.5">
                Stock Period <span className="text-red-500">*</span>
                {isPreviousAuditLocked && (
                  <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500" title="Previous audit date is sourced from the linked Stocktake / prior published report and cannot be edited. Stock period days starts at the calculated value but can be overridden — your entry saves and persists">
                    <Lock className="h-3 w-3" /> From Stocktake / prior report
                  </span>
                )}
              </Label>
              <p className="text-sm text-muted-foreground mb-2">
                {isPreviousAuditLocked
                  ? 'Previous audit date is sourced from the most recent prior audit for this site and cannot be edited. Stock period days defaults to the calculated figure below — enter a different value if needed.'
                  : 'First audit for this site — no previous period to source from. Set these manually.'}
              </p>
              {(() => {
                // 2026-04-28 — Three fields, fully independent after first
                // auto-population. The UI no longer cascades Previous Audit Date
                // changes into Stock Period Days (or vice versa). (Superseded
                // 2026-02 for the subset where a genuine source now exists —
                // see isPreviousAuditLocked above.)
                // 2026-02 correction: stock_period_days is editable whenever
                // canEdit is true, REGARDLESS of isPreviousAuditLocked — only
                // previous_audit_date (and audit_date, above) stay locked from
                // the Stocktake/prior report. The server computes/seeds the
                // calculated value as the visible default on every launch or
                // explicit refresh; a validated override here persists across
                // save/reopen (server no longer rejects it as a locked-field
                // violation — see _compute_locked_date_fields).
                const previousAuditDate = editablePreviousAuditDate;
                return (
                  <div className="flex flex-wrap items-end gap-4">
                    <div>
                      <Label htmlFor="previous-audit-date" className="text-xs text-muted-foreground">
                        Previous audit date
                      </Label>
                      {canEdit && !isPreviousAuditLocked ? (
                        <Input
                          id="previous-audit-date"
                          type="date"
                          value={previousAuditDate}
                          onChange={(e) => {
                            setEditablePreviousAuditDate(e.target.value);
                            setStockPeriodAutoPopulated(false);
                          }}
                          className="w-44"
                          data-testid="step1-previous-audit-date-input"
                        />
                      ) : (
                        <p className={`mt-1 font-medium ${isPreviousAuditLocked ? 'text-slate-500' : ''}`} data-testid="step1-previous-audit-date-readonly">
                          {previousAuditDate ? formatDate(previousAuditDate) : '—'}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="stock-period" className="text-xs text-muted-foreground">
                        Stock period days
                      </Label>
                      {canEdit ? (
                        <Input
                          id="stock-period"
                          type="number"
                          min="1"
                          max="365"
                          value={stockPeriodDays}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            setStockPeriodDays(Number.isFinite(n) ? n : '');
                            setStockPeriodAutoPopulated(false);
                          }}
                          className="w-32"
                          data-testid="step1-stock-period-input"
                        />
                      ) : (
                        <p className="font-medium" data-testid="step1-stock-period-readonly">{stockPeriodDays} days</p>
                      )}
                    </div>
                  </div>
                );
              })()}
              {stockPeriodDays !== '' && stockPeriodDays < 1 && (
                <p className="text-sm text-red-500 mt-1">Stock period must be greater than 0</p>
              )}
              {stockPeriodAutoPopulated && stockPeriodDays >= 1 && !isPreviousAuditLocked && (
                <p className="text-xs text-slate-500 italic mt-2" data-testid="step1-stock-period-autopopulated-hint">
                  Auto-populated from the most recent prior audit for this site. Adjust if needed — your entries save verbatim.
                </p>
              )}
              {isPreviousAuditLocked && canEdit && (
                <p className="text-xs text-slate-500 italic mt-2" data-testid="step1-stock-period-override-hint">
                  Calculated from the linked Stocktake's period — adjust the days above if needed; previous audit date stays locked and nothing in the Stocktake session, its snapshot, or the amounts above changes.
                </p>
              )}
            </div>
          </div>
        );


      case 'client_metrics':
        // Client Report Metrics (injected step for Beverage with template)
        // Determine column suffix from first metric's type
        const primaryType = capturedMetrics[0]?.metric_type_snapshot || 'PERCENTAGE';
        const colSuffix = getMetricColumnSuffix(primaryType);
        return (
          <div className="space-y-6" data-testid="client-metrics-step">
            <div>
              <h3 className="text-lg font-semibold">{metricsTemplate?.title || 'Client Metrics'}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Enter the metric values for this period. Last Period values are auto-populated from the most recent previous report where available.
              </p>
            </div>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 w-[30%]">Metric</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 w-[18%]">This Period {colSuffix}</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 w-[18%]">Last Period {colSuffix}</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 w-[14%]">Variance</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 w-[20%]">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {capturedMetrics.map((m, idx) => {
                    const thisVal = m.this_period_value;
                    const lastVal = m.last_period_value;
                    const mType = m.metric_type_snapshot || 'PERCENTAGE';
                    const varianceStr = formatVariance(thisVal, lastVal, mType);
                    const variance = (thisVal != null && lastVal != null) ? (thisVal - lastVal) : null;
                    return (
                      <tr key={idx} className="hover:bg-slate-50/50">
                        <td className="py-3 px-4 text-sm font-medium">{m.metric_label_snapshot}</td>
                        <td className="py-2 px-4">
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="0.0"
                            className="text-center h-9"
                            disabled={isPublished}
                            value={thisVal ?? ''}
                            onChange={e => {
                              const val = e.target.value === '' ? null : parseFloat(e.target.value);
                              setCapturedMetrics(prev => prev.map((cm, i) => i === idx ? { ...cm, this_period_value: val } : cm));
                              setHasUnsavedChanges(true);
                            }}
                            data-testid={`metric-this-${idx}`}
                          />
                        </td>
                        <td className="py-2 px-4">
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="0.0"
                            className="text-center h-9"
                            disabled={isPublished}
                            value={lastVal ?? ''}
                            onChange={e => {
                              const val = e.target.value === '' ? null : parseFloat(e.target.value);
                              setCapturedMetrics(prev => prev.map((cm, i) => i === idx ? { ...cm, last_period_value: val } : cm));
                              setHasUnsavedChanges(true);
                            }}
                            data-testid={`metric-last-${idx}`}
                          />
                        </td>
                        <td className="py-3 px-4 text-center text-sm">
                          {varianceStr ? (
                            <span className={`font-medium ${variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                              {varianceStr}
                            </span>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="py-2 px-4">
                          {m.note_enabled_snapshot ? (
                            <Input
                              placeholder="Add note..."
                              className="h-9 text-sm"
                              disabled={isPublished}
                              value={m.note || ''}
                              onChange={e => {
                                setCapturedMetrics(prev => prev.map((cm, i) => i === idx ? { ...cm, note: e.target.value } : cm));
                                setHasUnsavedChanges(true);
                              }}
                              data-testid={`metric-note-${idx}`}
                            />
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {capturedMetrics.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">No metrics defined in the template.</div>
            )}
          </div>
        );

      case 'kpi':
        // Step 2: Key Performance Indicators (Updated with Result)
        const metrics = sections.metrics || {};
        const history = sections.history || { items: [] };
        const historyItems = history.items || [];
        
        const addHistoryItem = () => {
          if (historyItems.length >= MAX_HISTORY_ITEMS) {
            toast.error(`Maximum ${MAX_HISTORY_ITEMS} previous audits allowed`);
            return;
          }
          const newItems = [...historyItems, { 
            audit_date: '', 
            stockholding_value: '', 
            stockholding_days: '',
            sales_for_period: '',
            gp_percent: '',
            cos_percent: '',
            gp_before_allowances_percent: '',
            cos_before_allowances_percent: '',
            wastage_value: '',
            stocktake_result_value: '',
            stocktake_result_percent: '',
            exit_meeting_attended: null,
            yield_percent: ''
          }];
          updateSection('history', { items: newItems });
        };
        
        const updateHistoryItem = (index, field, value) => {
          // Validate date field
          if (field === 'audit_date' && value) {
            const auditDate = report?.audit_date;
            // Check 1: date must be before the current audit date
            if (auditDate && value >= auditDate) {
              toast.error('Historical date must be before the current audit date (' + new Date(auditDate + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ')');
              return;
            }
            // Check 2: no duplicate dates in history
            const duplicate = historyItems.some((item, idx) => idx !== index && item.audit_date === value);
            if (duplicate) {
              toast.error('A historical entry with this date already exists. Each historical line must have a unique date.');
              return;
            }
          }
          const newItems = [...historyItems];
          newItems[index] = { ...newItems[index], [field]: value };
          updateSection('history', { items: newItems });
        };
        
        const removeHistoryItem = (index) => {
          const newItems = [...historyItems];
          newItems.splice(index, 1);
          updateSection('history', { items: newItems });
        };
        
        return (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Enter the key performance indicators for this {scopeInfo.label.toLowerCase()} stock audit.
            </p>
            
            {/* Current Audit Metrics - This Period */}
            <div className="border rounded-lg p-4 bg-slate-50/50">
              <h3 className="font-semibold text-sm mb-4 flex items-center gap-2 text-blue-700">
                <Briefcase className="h-4 w-4" />
                This Period (Current Audit)
              </h3>
              
              {/* Row 1: Core Financial Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <Label htmlFor="stockholding-value" className="text-sm font-medium flex items-center gap-1.5">
                    Stockholding £ <span className="text-red-500">*</span>
                    {isFieldLocked('metrics.stockholding_value') && (
                      <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500" data-testid="locked-badge-stockholding-value" title={lockedFieldHint}>
                        <Lock className="h-3 w-3" /> Locked
                      </span>
                    )}
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-muted-foreground">£</span>
                    <Input
                      id="stockholding-value"
                      type="number"
                      step="0.01"
                      min="0"
                      value={metrics.stockholding_value || ''}
                      onChange={(e) => updateSection('metrics', { ...metrics, stockholding_value: e.target.value })}
                      disabled={!canEdit || isFieldLocked('metrics.stockholding_value')}
                      className="w-full"
                      placeholder="0.00"
                      data-testid="step2-stockholding-value"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="stockholding-days" className="text-sm font-medium flex items-center gap-1.5">
                    Stockholding Days (Actual)
                    {isFieldLocked('metrics.stockholding_days') && (
                      <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500" data-testid="locked-badge-stockholding-days" title={lockedFieldHint}>
                        <Lock className="h-3 w-3" /> Locked
                      </span>
                    )}
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      id="stockholding-days"
                      type="number"
                      step="1"
                      min="0"
                      value={metrics.stockholding_days || ''}
                      onChange={(e) => updateSection('metrics', { ...metrics, stockholding_days: e.target.value })}
                      disabled={!canEdit || isFieldLocked('metrics.stockholding_days')}
                      className="w-full"
                      placeholder="0"
                      data-testid="step2-stockholding-days"
                    />
                    <span className="text-muted-foreground text-sm">days</span>
                  </div>
                  {/* Show target from site master and variance */}
                  {report?.site_target_stockholding_days != null && (
                    <p className="text-xs mt-1">
                      <span className="text-muted-foreground">Target: {report.site_target_stockholding_days} days</span>
                      {metrics.stockholding_days && (
                        <span className={`ml-2 ${
                          parseInt(metrics.stockholding_days) <= report.site_target_stockholding_days 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>
                          ({parseInt(metrics.stockholding_days) <= report.site_target_stockholding_days ? '✓ On target' : `+${parseInt(metrics.stockholding_days) - report.site_target_stockholding_days} over`})
                        </span>
                      )}
                    </p>
                  )}
                  {report?.site_target_stockholding_days == null && (
                    <p className="text-xs text-amber-600 mt-1">Target not configured in site master</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="sales-for-period" className="text-sm font-medium flex items-center gap-1.5">
                    Sales for Period £
                    {isFieldLocked('metrics.sales_for_period') && (
                      <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500" data-testid="locked-badge-sales-for-period" title={lockedFieldHint}>
                        <Lock className="h-3 w-3" /> Locked
                      </span>
                    )}
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-muted-foreground">£</span>
                    <Input
                      id="sales-for-period"
                      type="number"
                      step="0.01"
                      min="0"
                      value={metrics.sales_for_period || ''}
                      onChange={(e) => updateSection('metrics', { ...metrics, sales_for_period: e.target.value })}
                      disabled={!canEdit || isFieldLocked('metrics.sales_for_period')}
                      className="w-full"
                      placeholder="0.00"
                      data-testid="step2-sales-for-period"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="gp-percent" className="text-sm font-medium flex items-center gap-1.5">
                    {isFoodOrShopScope ? 'GP % Before Allowances' : 'GP %'} <span className="text-red-500">*</span>
                    {!isFoodOrShopScope && isFieldLocked('metrics.gp_percent') && (
                      <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500" data-testid="locked-badge-gp-percent" title={lockedFieldHint}>
                        <Lock className="h-3 w-3" /> Locked
                      </span>
                    )}
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      id="gp-percent"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={isFoodOrShopScope ? (metrics.gp_before_allowances_percent || '') : (metrics.gp_percent || '')}
                      onChange={(e) => updateSection('metrics', { ...metrics, [isFoodOrShopScope ? 'gp_before_allowances_percent' : 'gp_percent']: e.target.value })}
                      disabled={!canEdit || (!isFoodOrShopScope && isFieldLocked('metrics.gp_percent'))}
                      className="w-full"
                      placeholder="0.00"
                      data-testid="step2-gp-percent"
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                </div>
              </div>
              
              {/* Row 2: COS, Wastage */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <Label htmlFor="cos-percent" className="text-sm font-medium flex items-center gap-1.5">
                    {isFoodOrShopScope ? 'COS % Before Allowances' : 'Cost of Sales (COS) %'}
                    {!isFoodOrShopScope && isFieldLocked('metrics.cos_percent') && (
                      <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500" data-testid="locked-badge-cos-percent" title={lockedFieldHint}>
                        <Lock className="h-3 w-3" /> Locked
                      </span>
                    )}
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      id="cos-percent"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={isFoodOrShopScope ? (metrics.cos_before_allowances_percent || '') : (metrics.cos_percent || '')}
                      onChange={(e) => updateSection('metrics', { ...metrics, [isFoodOrShopScope ? 'cos_before_allowances_percent' : 'cos_percent']: e.target.value })}
                      disabled={!canEdit || (!isFoodOrShopScope && isFieldLocked('metrics.cos_percent'))}
                      className="w-full"
                      placeholder="0.00"
                      data-testid="step2-cos-percent"
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                </div>
                
                {/* Display COS Budget % from site master (read-only) */}
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1.5 text-muted-foreground">
                    COS Budget %
                    <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500">
                      <Lock className="h-3 w-3" /> From Site
                    </span>
                  </Label>
                  <div className="flex items-center gap-2 mt-1 h-9 px-3 rounded-md border bg-slate-100">
                    <span className="text-slate-600 font-medium">
                      {report?.site_budget_cos_pct != null ? `${report.site_budget_cos_pct}%` : '—'}
                    </span>
                    {report?.site_budget_cos_pct == null && (
                      <span className="text-amber-600 text-xs">Not configured</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Target COS % from site master</p>
                </div>
                
                {/* Food/Shop-specific: GP % and COS % After Allowances */}
                {isFoodOrShopScope && (
                  <>
                    <div>
                      <Label htmlFor="gp-after-allowances" className="text-sm font-medium">
                        GP % After Allowances
                      </Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          id="gp-after-allowances"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={metrics.gp_percent || ''}
                          onChange={(e) => updateSection('metrics', { ...metrics, gp_percent: e.target.value })}
                          disabled={!canEdit}
                          className="w-full"
                          placeholder="0.00"
                          data-testid="step2-gp-after-allowances"
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="cos-after-allowances" className="text-sm font-medium">
                        COS % After Allowances
                      </Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          id="cos-after-allowances"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={metrics.cos_percent || ''}
                          onChange={(e) => updateSection('metrics', { ...metrics, cos_percent: e.target.value })}
                          disabled={!canEdit}
                          className="w-full"
                          placeholder="0.00"
                          data-testid="step2-cos-after-allowances"
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                  </>
                )}
                
                <div>
                  <Label htmlFor="wastage-value" className="text-sm font-medium flex items-center gap-1.5">
                    Wastage £ <span className="text-red-500">*</span>
                    {isFieldLocked('metrics.wastage_value') && (
                      <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500" data-testid="locked-badge-wastage-value" title={lockedFieldHint}>
                        <Lock className="h-3 w-3" /> Locked
                      </span>
                    )}
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-muted-foreground">£</span>
                    <Input
                      id="wastage-value"
                      type="number"
                      step="0.01"
                      min="0"
                      value={metrics.wastage_value || ''}
                      onChange={(e) => updateSection('metrics', { ...metrics, wastage_value: e.target.value })}
                      disabled={!canEdit || isFieldLocked('metrics.wastage_value')}
                      className="w-full"
                      placeholder="0.00"
                      data-testid="step2-wastage-value"
                    />
                  </div>
                </div>
                
                {/* Result - Beverage scope only */}
                {!isFoodOrShopScope && (
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-1.5">
                      Result
                      {(isFieldLocked('metrics.stocktake_result_value') || isFieldLocked('metrics.stocktake_result_percent')) && (
                        <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500" data-testid="locked-badge-stocktake-result" title={lockedFieldHint}>
                          <Lock className="h-3 w-3" /> Locked
                        </span>
                      )}
                    </Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground text-xs">£</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={metrics.stocktake_result_value || ''}
                          onChange={(e) => updateSection('metrics', { ...metrics, stocktake_result_value: e.target.value })}
                          disabled={!canEdit || isFieldLocked('metrics.stocktake_result_value')}
                          className="w-full"
                          placeholder="±0.00"
                          data-testid="step2-stocktake-result-value"
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          step="0.01"
                          value={metrics.stocktake_result_percent || ''}
                          onChange={(e) => updateSection('metrics', { ...metrics, stocktake_result_percent: e.target.value })}
                          disabled={!canEdit || isFieldLocked('metrics.stocktake_result_percent')}
                          className="w-full"
                          placeholder="±0.00"
                          data-testid="step2-stocktake-result-percent"
                        />
                        <span className="text-muted-foreground text-xs">%</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Positive or negative values allowed</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="col-span-1 md:col-span-2 lg:col-span-1">
                  <Label className="text-sm font-medium">
                    Exit Meeting Attendance
                  </Label>
                  <div className="flex items-center gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="exit-meeting"
                        checked={metrics.exit_meeting_attended === true}
                        onChange={() => updateSection('metrics', { ...metrics, exit_meeting_attended: true })}
                        disabled={!canEdit}
                        className="w-4 h-4 text-green-600"
                        data-testid="step2-exit-meeting-attended"
                      />
                      <span className="text-sm text-green-700">Attended</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="exit-meeting"
                        checked={metrics.exit_meeting_attended === false}
                        onChange={() => updateSection('metrics', { ...metrics, exit_meeting_attended: false })}
                        disabled={!canEdit}
                        className="w-4 h-4 text-red-600"
                        data-testid="step2-exit-meeting-not-attended"
                      />
                      <span className="text-sm text-red-700">Not Attended</span>
                    </label>
                  </div>
                </div>
                
                {/* Yield % - Beverage scope only */}
                {!isFoodOrShopScope && (
                  <div>
                    <Label htmlFor="yield-percent" className="text-sm font-medium flex items-center gap-1.5">
                      Yield %
                      {isFieldLocked('metrics.yield_percent') && (
                        <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500" data-testid="locked-badge-yield-percent" title={lockedFieldHint}>
                          <Lock className="h-3 w-3" /> Locked
                        </span>
                      )}
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        id="yield-percent"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={metrics.yield_percent || ''}
                        onChange={(e) => updateSection('metrics', { ...metrics, yield_percent: e.target.value })}
                        disabled={!canEdit || isFieldLocked('metrics.yield_percent')}
                        className="w-full"
                        placeholder="0.00"
                        data-testid="step2-yield-percent"
                      />
                      <span className="text-muted-foreground">%</span>
                    </div>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="gross-profit-amount" className="text-sm font-medium flex items-center gap-1.5">
                    Gross Profit Amount
                    {isFieldLocked('metrics.gross_profit_amount') && (
                      <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500" data-testid="locked-badge-gross-profit-amount" title={lockedFieldHint}>
                        <Lock className="h-3 w-3" /> Locked
                      </span>
                    )}
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-muted-foreground">£</span>
                    <Input
                      id="gross-profit-amount"
                      type="number"
                      step="0.01"
                      value={metrics.gross_profit_amount || ''}
                      onChange={(e) => updateSection('metrics', { ...metrics, gross_profit_amount: e.target.value })}
                      disabled={!canEdit || isFieldLocked('metrics.gross_profit_amount')}
                      className="w-full"
                      placeholder="0.00"
                      data-testid="step2-gross-profit-amount"
                    />
                  </div>
                </div>
              </div>
              
              {/* Year-to-Date Section */}
              <div className="mt-4 pt-4 border-t border-dashed border-slate-300">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Year to Date</h4>

                {priorYtdInfo?.ytd_basis === 'fy_reset' && (
                  <div className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="ytd-fy-reset-banner">
                    <span className="font-medium">Opening YTD reset to £0.00 — new financial year from {formatDate(priorYtdInfo.fy_start)}.</span>
                    <span className="block mt-0.5 text-slate-400">{priorYtdInfo.reason}</span>
                  </div>
                )}
                {priorYtdInfo?.ytd_basis === 'unresolved' && (
                  <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="ytd-unresolved-banner">
                    <span className="font-medium">Opening YTD needs review — not carried forward and not assumed to be zero.</span>
                    <span className="block mt-0.5">{priorYtdInfo.reason}</span>
                    {report?.is_editable && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-2 h-7 text-xs bg-white"
                        onClick={handleRecalculateOpeningYtd}
                        disabled={recalculatingYtd}
                        data-testid="ytd-recalculate-btn"
                      >
                        {recalculatingYtd ? 'Recalculating…' : 'Recalculate opening YTD now'}
                      </Button>
                    )}
                  </div>
                )}
                {/* Opening YTD banner — shown for later audits (carry-forward) */}
                {priorYtdInfo && !priorYtdInfo.is_first_audit && priorYtdInfo.ytd_basis !== 'fy_reset' && (
                  <div className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 flex items-start gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <div>
                      <span className="font-medium text-slate-600">Opening YTD carried from prior audit
                        {priorYtdInfo.prior_audit_date ? ` (${priorYtdInfo.prior_audit_date})` : ''}:
                      </span>
                      {/* 2026-02 — a blank prior_ytd figure means unresolved
                          (e.g. that prior report's own result was withheld),
                          never a real £0; show "pending" rather than a false
                          total. Negative amounts render as "-£x" not the
                          confusing "£-x". */}
                      {' '}Sales {priorYtdInfo.prior_ytd?.ytd_sales_amount ? fmtGbp(priorYtdInfo.prior_ytd.ytd_sales_amount) : '(pending)'}
                      {priorYtdInfo.prior_ytd?.ytd_gross_profit_amount ? ` · GP ${fmtGbp(priorYtdInfo.prior_ytd.ytd_gross_profit_amount)}` : ''}
                      {priorYtdInfo.prior_ytd?.ytd_result_amount ? ` · Result ${fmtGbp(priorYtdInfo.prior_ytd.ytd_result_amount)}` : ' · Result (pending — not yet available from the prior audit)'}
                      <span className="block mt-0.5 text-slate-400">Fields update automatically as you enter this period's figures above.</span>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="ytd-sales" className="text-sm font-medium">Sales Amount</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-muted-foreground">£</span>
                      <Input
                        id="ytd-sales"
                        type="number"
                        step="0.01"
                        min="0"
                        value={metrics.ytd_sales_amount || ''}
                        onChange={(e) => updateSection('metrics', { ...metrics, ytd_sales_amount: e.target.value })}
                        disabled={!canEdit || (priorYtdInfo && !priorYtdInfo.is_first_audit)}
                        className={`w-full ${priorYtdInfo && !priorYtdInfo.is_first_audit ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                        placeholder="0.00"
                        data-testid="step2-ytd-sales-amount"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="ytd-gp-amount" className="text-sm font-medium">Gross Profit Amount</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-muted-foreground">£</span>
                      <Input
                        id="ytd-gp-amount"
                        type="number"
                        step="0.01"
                        value={metrics.ytd_gross_profit_amount || ''}
                        onChange={(e) => updateSection('metrics', { ...metrics, ytd_gross_profit_amount: e.target.value })}
                        disabled={!canEdit || (priorYtdInfo && !priorYtdInfo.is_first_audit)}
                        className={`w-full ${priorYtdInfo && !priorYtdInfo.is_first_audit ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                        placeholder="0.00"
                        data-testid="step2-ytd-gross-profit-amount"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="ytd-gp-percent" className="text-sm font-medium">Gross Profit %</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        id="ytd-gp-percent"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={metrics.ytd_gross_profit_percent || ''}
                        onChange={(e) => updateSection('metrics', { ...metrics, ytd_gross_profit_percent: e.target.value })}
                        disabled={!canEdit}
                        className="w-full"
                        placeholder="0.00"
                        data-testid="step2-ytd-gross-profit-percent"
                      />
                      <span className="text-muted-foreground">%</span>
                    </div>
                  </div>
                  
                  {/* Result Amount, Result %, Yield % - Beverage scope only */}
                  {!isFoodOrShopScope && (
                    <>
                      <div>
                        <Label htmlFor="ytd-result-amount" className="text-sm font-medium">Result Amount</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-muted-foreground">£</span>
                          <Input
                            id="ytd-result-amount"
                            type="number"
                            step="0.01"
                            value={metrics.ytd_result_amount || ''}
                            onChange={(e) => updateSection('metrics', { ...metrics, ytd_result_amount: e.target.value })}
                            disabled={!canEdit || (priorYtdInfo && !priorYtdInfo.is_first_audit)}
                            className={`w-full ${priorYtdInfo && !priorYtdInfo.is_first_audit ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                            placeholder="±0.00"
                            data-testid="step2-ytd-result-amount"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="ytd-result-percent" className="text-sm font-medium">Result %</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            id="ytd-result-percent"
                            type="number"
                            step="0.01"
                            value={metrics.ytd_result_percent || ''}
                            onChange={(e) => updateSection('metrics', { ...metrics, ytd_result_percent: e.target.value })}
                            disabled={!canEdit}
                            className="w-full"
                            placeholder="±0.00"
                            data-testid="step2-ytd-result-percent"
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="ytd-yield" className="text-sm font-medium">Yield %</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            id="ytd-yield"
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={metrics.ytd_yield_percent || ''}
                            onChange={(e) => updateSection('metrics', { ...metrics, ytd_yield_percent: e.target.value })}
                            disabled={!canEdit}
                            className="w-full"
                            placeholder="0.00"
                            data-testid="step2-ytd-yield-percent"
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>
            </div>
            
            {/* Previous Audits History — auto-opens and shows banner when pre-populated */}
            <div className="border-t pt-6">
              {/* Auto-populated banner */}
              {historyAutoPopulated && historyItems.length > 0 && (
                <div className="flex items-start gap-3 p-3 mb-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="history-prepopulated-banner">
                  <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">
                      {historyItems.length} previous audit{historyItems.length > 1 ? 's' : ''} detected and pre-populated
                    </p>
                    <p className="text-xs text-blue-600 mt-0.5">
                      Historical trend data from prior audits for this site has been automatically loaded below. You can review or edit these values.
                    </p>
                  </div>
                </div>
              )}

              <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
                <CollapsibleTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="w-full flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg border"
                    data-testid="history-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <History className="h-4 w-4 text-slate-500" />
                      <span className="font-medium">Previous Audits</span>
                      <Badge variant={historyItems.length > 0 ? "default" : "secondary"} className="ml-2">
                        {historyItems.length}/{MAX_HISTORY_ITEMS}
                      </Badge>
                    </div>
                    {historyOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4">
                  <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                    <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
                      <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                      <span>Historical results refer to results <strong>prior to this audit date</strong> ({report?.audit_date ? new Date(report.audit_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}). Do not re-enter the current audit result here.</span>
                    </div>
                    {historyItems.length === 0 ? (
                      <div className="text-center py-6 bg-white rounded-lg border border-dashed">
                        <History className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No previous audit data added</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Add up to {MAX_HISTORY_ITEMS} previous audit data points for trend comparison.
                        </p>
                        {canEdit && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addHistoryItem}
                            className="mt-3"
                            data-testid="add-first-history-btn"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Previous Audit
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Compact summary table of all history items */}
                        <div className="overflow-x-auto bg-white rounded-lg border">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-50 border-b">
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">#</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Audit Date</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Stock Value</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Stock Days</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Sales</th>
                                {isFoodOrShopScope && <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">GP% Bef.</th>}
                                {isFoodOrShopScope && <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">COS% Bef.</th>}
                                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">{isFoodOrShopScope ? 'GP% Aft.' : 'GP %'}</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">{isFoodOrShopScope ? 'COS% Aft.' : 'COS %'}</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Stocktake +/-</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Stocktake %</th>
                                <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600">Meeting</th>
                                {!isFoodOrShopScope && <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Yield %</th>}
                                {canEdit && <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600 w-10"></th>}
                              </tr>
                            </thead>
                            <tbody>
                              {historyItems.map((item, idx) => (
                                <tr key={idx} className="border-b last:border-b-0 hover:bg-slate-50" data-testid={`history-row-${idx}`}>
                                  <td className="px-3 py-2 text-xs text-slate-400">{idx + 1}</td>
                                  <td className="px-3 py-2 text-xs font-medium">
                                    {item.audit_date ? new Date(item.audit_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-right tabular-nums">
                                    {item.stockholding_value ? `£${Number(item.stockholding_value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-right tabular-nums">{item.stockholding_days || '—'}</td>
                                  <td className="px-3 py-2 text-xs text-right tabular-nums">
                                    {item.sales_for_period ? `£${Number(item.sales_for_period).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                                  </td>
                                  {isFoodOrShopScope && <td className="px-3 py-2 text-xs text-right tabular-nums">{item.gp_before_allowances_percent ? `${item.gp_before_allowances_percent}%` : '—'}</td>}
                                  {isFoodOrShopScope && <td className="px-3 py-2 text-xs text-right tabular-nums">{item.cos_before_allowances_percent ? `${item.cos_before_allowances_percent}%` : '—'}</td>}
                                  <td className="px-3 py-2 text-xs text-right tabular-nums">{item.gp_percent ? `${item.gp_percent}%` : '—'}</td>
                                  <td className="px-3 py-2 text-xs text-right tabular-nums">{item.cos_percent ? `${item.cos_percent}%` : '—'}</td>
                                  <td className="px-3 py-2 text-xs text-right tabular-nums">
                                    {item.stocktake_result_value ? `£${Number(item.stocktake_result_value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-right tabular-nums">
                                    {item.stocktake_result_percent != null && item.stocktake_result_percent !== '' ? `${item.stocktake_result_percent}%` : '—'}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-center">
                                    {item.exit_meeting_attended === true ? 'Yes' : item.exit_meeting_attended === false ? 'No' : '—'}
                                  </td>
                                  {!isFoodOrShopScope && (
                                    <td className="px-3 py-2 text-xs text-right tabular-nums">
                                      {item.yield_percent ? `${item.yield_percent}%` : '—'}
                                    </td>
                                  )}
                                  {canEdit && (
                                    <td className="px-3 py-2 text-center">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeHistoryItem(idx)}
                                        className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                                        data-testid={`history-${idx}-delete`}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Editable detail cards (expandable per item) */}
                        {canEdit && (
                          <details className="text-sm">
                            <summary className="cursor-pointer text-xs text-blue-600 hover:text-blue-800 font-medium py-1">
                              Edit individual audit values...
                            </summary>
                            <div className="mt-3 space-y-3">
                              {historyItems.map((item, idx) => {
                                return (
                                <div key={idx} className="bg-white rounded-lg border p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-medium text-slate-600">
                                      Audit #{idx + 1} — {item.audit_date ? new Date(item.audit_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'No date'}
                                    </span>
                                  </div>
                                  
                                  {/* Row 1: Date, Stockholding Value, Stockholding Days, Sales */}
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                                    <div>
                                      <Label className="text-xs text-muted-foreground">Audit Date</Label>
                                      <Input
                                        type="date"
                                        value={item.audit_date || ''}
                                        onChange={(e) => updateHistoryItem(idx, 'audit_date', e.target.value)}
                                        className="h-9 text-sm"
                                        data-testid={`history-${idx}-date`}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground">Stockholding £</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={item.stockholding_value || ''}
                                        onChange={(e) => updateHistoryItem(idx, 'stockholding_value', e.target.value)}
                                        placeholder="0.00"
                                        className="h-9 text-sm"
                                        data-testid={`history-${idx}-stockholding`}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground">Stockholding Days</Label>
                                      <Input
                                        type="number"
                                        step="1"
                                        min="0"
                                        value={item.stockholding_days || ''}
                                        onChange={(e) => updateHistoryItem(idx, 'stockholding_days', e.target.value)}
                                        placeholder="0"
                                        className="h-9 text-sm"
                                        data-testid={`history-${idx}-stockholding-days`}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground">Sales £</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={item.sales_for_period || ''}
                                        onChange={(e) => updateHistoryItem(idx, 'sales_for_period', e.target.value)}
                                        placeholder="0.00"
                                        className="h-9 text-sm"
                                        data-testid={`history-${idx}-sales`}
                                      />
                                    </div>
                                  </div>
                                  
                                  {/* Row 2: GP, COS %, Wastage, Stocktake */}
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                                    {isFoodOrShopScope && (
                                      <div>
                                        <Label className="text-xs text-muted-foreground">GP % Before Allowances</Label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          max="100"
                                          value={item.gp_before_allowances_percent || ''}
                                          onChange={(e) => updateHistoryItem(idx, 'gp_before_allowances_percent', e.target.value)}
                                          placeholder="0.00"
                                          className="h-9 text-sm"
                                          data-testid={`history-${idx}-gp-before`}
                                        />
                                      </div>
                                    )}
                                    {isFoodOrShopScope && (
                                      <div>
                                        <Label className="text-xs text-muted-foreground">COS % Before Allowances</Label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          max="100"
                                          value={item.cos_before_allowances_percent || ''}
                                          onChange={(e) => updateHistoryItem(idx, 'cos_before_allowances_percent', e.target.value)}
                                          placeholder="0.00"
                                          className="h-9 text-sm"
                                          data-testid={`history-${idx}-cos-before`}
                                        />
                                      </div>
                                    )}
                                    <div>
                                      <Label className="text-xs text-muted-foreground">{isFoodOrShopScope ? 'GP % After Allowances' : 'GP %'}</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        value={item.gp_percent || ''}
                                        onChange={(e) => updateHistoryItem(idx, 'gp_percent', e.target.value)}
                                        placeholder="0.00"
                                        className="h-9 text-sm"
                                        data-testid={`history-${idx}-gp`}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground">{isFoodOrShopScope ? 'COS % After Allowances' : 'COS %'}</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        value={item.cos_percent || ''}
                                        onChange={(e) => updateHistoryItem(idx, 'cos_percent', e.target.value)}
                                        placeholder="0.00"
                                        className="h-9 text-sm"
                                        data-testid={`history-${idx}-cos`}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground">Wastage £</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={item.wastage_value || ''}
                                        onChange={(e) => updateHistoryItem(idx, 'wastage_value', e.target.value)}
                                        placeholder="0.00"
                                        className="h-9 text-sm"
                                        data-testid={`history-${idx}-wastage`}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground">Stocktake £ (+/-)</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={item.stocktake_result_value || ''}
                                        onChange={(e) => updateHistoryItem(idx, 'stocktake_result_value', e.target.value)}
                                        placeholder="±0.00"
                                        className="h-9 text-sm"
                                        data-testid={`history-${idx}-stocktake-value`}
                                      />
                                    </div>
                                  </div>
                                  
                                  {/* Row 3: Stocktake %, Meeting Attended */}
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div>
                                      <Label className="text-xs text-muted-foreground">Result %</Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={item.stocktake_result_percent || ''}
                                        onChange={(e) => updateHistoryItem(idx, 'stocktake_result_percent', e.target.value)}
                                        placeholder="0.00"
                                        className="h-9 text-sm"
                                        data-testid={`history-${idx}-stocktake-percent`}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground">Exit Meeting Attended</Label>
                                      <Select
                                        value={item.exit_meeting_attended === true ? 'yes' : item.exit_meeting_attended === false ? 'no' : 'unset'}
                                        onValueChange={(val) => updateHistoryItem(idx, 'exit_meeting_attended', val === 'yes' ? true : val === 'no' ? false : null)}
                                      >
                                        <SelectTrigger className="h-9 text-sm" data-testid={`history-${idx}-meeting`}>
                                          <SelectValue placeholder="Select..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="unset">Not Set</SelectItem>
                                          <SelectItem value="yes">Attended</SelectItem>
                                          <SelectItem value="no">Not Attended</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    {!isFoodOrShopScope && (
                                      <div>
                                        <Label className="text-xs text-muted-foreground">Yield %</Label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          max="200"
                                          value={item.yield_percent || ''}
                                          onChange={(e) => updateHistoryItem(idx, 'yield_percent', e.target.value)}
                                          placeholder="0.00"
                                          className="h-9 text-sm"
                                          data-testid={`history-${idx}-yield`}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )})}
                            </div>
                          </details>
                        )}
                        
                        {canEdit && historyItems.length < MAX_HISTORY_ITEMS && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addHistoryItem}
                            className="w-full"
                            data-testid="add-history-btn"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Previous Audit ({historyItems.length}/{MAX_HISTORY_ITEMS})
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        );

      case 'variance':
        // Step 3: Variance Highlights - Enhanced with Quantity, Measure, Price
        const variance = sections.variance || { shortages: [], surpluses: [] };
        // Dynamic measures from central measure_definitions table (fetched on mount)
        
        const addVarianceItem = (type) => {
          const items = variance[type] || [];
          if (items.length >= 5) {
            toast.error(`Maximum ${type} is 5 items`);
            return;
          }
          updateSection('variance', {
            ...variance,
            [type]: [...items, { item_name: '', quantity: '', measure: '', price: '', at_retail: '', optional_note: '' }]
          });
        };
        
        const updateVarianceItem = (type, index, field, value) => {
          const items = [...(variance[type] || [])];
          let newValue = value;
          items[index] = { ...items[index], [field]: newValue };
          
          // Auto-calculate at_retail = quantity * price (with sign enforcement)
          const item = items[index];
          const qty = parseFloat(item.quantity) || 0;
          const price = parseFloat(item.price) || 0;
          if (qty > 0 && price > 0) {
            let amount = qty * price;
            // Enforce sign: shortages negative, surpluses positive
            if (type === 'shortages') amount = -Math.abs(amount);
            if (type === 'surpluses') amount = Math.abs(amount);
            items[index].at_retail = String(Math.round(amount * 100) / 100);
          }
          
          updateSection('variance', { ...variance, [type]: items });
        };
        
        const removeVarianceItem = (type, index) => {
          const items = [...(variance[type] || [])];
          items.splice(index, 1);
          updateSection('variance', { ...variance, [type]: items });
        };
        
        // Detect if item is legacy (no quantity/price fields)
        const isLegacyItem = (item) => !item.quantity && !item.price && item.at_retail;
        
        const renderVarianceTable = (type, title, icon, colorClass) => {
          const items = variance[type] || [];
          
          return (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className={`font-medium flex items-center gap-2 ${colorClass}`}>
                  {icon}
                  {title} ({items.length}/5)
                </h4>
                {canEdit && items.length < 5 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addVarianceItem(type)}
                    data-testid={`add-${type}-btn`}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                )}
              </div>
              
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground italic py-4 text-center bg-slate-50 rounded">
                  No {type} added yet
                </p>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-2 py-2 text-left font-medium">Description</th>
                        <th className="px-2 py-2 text-left font-medium w-20">Qty</th>
                        <th className="px-2 py-2 text-left font-medium w-28">Measure</th>
                        <th className="px-2 py-2 text-left font-medium w-24">Price</th>
                        <th className="px-2 py-2 text-left font-medium w-24">Amount</th>
                        <th className="px-2 py-2 text-left font-medium">Comments</th>
                        {canEdit && <th className="px-2 py-2 w-10"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => {
                        const legacy = isLegacyItem(item);
                        return (
                          <tr key={idx} className="border-t">
                            <td className="px-2 py-2">
                              <Input
                                value={item.item_name || ''}
                                onChange={(e) => updateVarianceItem(type, idx, 'item_name', e.target.value)}
                                disabled={!canEdit}
                                placeholder="Item name"
                                className="h-8"
                                data-testid={`${type}-${idx}-name`}
                              />
                            </td>
                            {legacy ? (
                              <>
                                <td className="px-2 py-2 text-center text-xs text-muted-foreground" colSpan={3}>
                                  <span className="text-xs italic">Legacy entry</span>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-2 py-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={item.quantity || ''}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === '' || (Number.isInteger(Number(v)) && Number(v) >= 0)) {
                                        updateVarianceItem(type, idx, 'quantity', v);
                                      }
                                    }}
                                    disabled={!canEdit}
                                    placeholder="0"
                                    className="h-8"
                                    data-testid={`${type}-${idx}-qty`}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <Select
                                    value={item.measure || ''}
                                    onValueChange={(v) => updateVarianceItem(type, idx, 'measure', v)}
                                    disabled={!canEdit}
                                  >
                                    <SelectTrigger className="h-8 text-xs" data-testid={`${type}-${idx}-measure`}>
                                      <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {measureOptions.map(m => (
                                        <SelectItem key={m} value={m}>{m}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="px-2 py-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.price || ''}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === '' || Number(v) >= 0) {
                                        updateVarianceItem(type, idx, 'price', v);
                                      }
                                    }}
                                    disabled={!canEdit}
                                    placeholder="0.00"
                                    className="h-8"
                                    data-testid={`${type}-${idx}-price`}
                                  />
                                </td>
                              </>
                            )}
                            <td className="px-2 py-2">
                              <span className="text-sm font-medium" data-testid={`${type}-${idx}-amount`}>
                                {item.at_retail ? formatCurrency(item.at_retail, currencySymbol, 2) : '—'}
                              </span>
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                value={item.optional_note || ''}
                                onChange={(e) => updateVarianceItem(type, idx, 'optional_note', e.target.value)}
                                disabled={!canEdit}
                                placeholder="Optional"
                                className="h-8"
                                data-testid={`${type}-${idx}-note`}
                              />
                            </td>
                            {canEdit && (
                              <td className="px-2 py-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeVarianceItem(type, idx)}
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                  data-testid={`${type}-${idx}-delete`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        };
        
        return (
          <div className="space-y-8">
            <p className="text-sm text-muted-foreground">
              Record the top shortages and surpluses identified during the audit. Maximum 5 each.
              Enter quantity, measure, and unit price — the amount will be calculated automatically.
            </p>
            
            {renderVarianceTable(
              'shortages',
              'Top Shortages',
              <TrendingDown className="h-4 w-4" />,
              'text-red-600'
            )}
            
            {renderVarianceTable(
              'surpluses',
              'Top Surpluses',
              <TrendingUp className="h-4 w-4" />,
              'text-green-600'
            )}
          </div>
        );

      case 'allowances':
        // Step 4: Allowances & Management
        const allowances = sections.allowances || [];
        const allowancesUncategorised = sections.allowances_uncategorised || [];
        const management = sections.management || [];
        
        const addAllowance = () => {
          updateSection('allowances', [...allowances, { description: '', at_retail: '' }]);
        };
        
        const updateAllowance = (index, field, value) => {
          if (allowances[index]?.source_entry_id != null) return; // imported row — server-protected, no client bypass
          const items = [...allowances];
          items[index] = { ...items[index], [field]: value };
          updateSection('allowances', items);
        };
        
        const removeAllowance = (index) => {
          if (allowances[index]?.source_entry_id != null) return;
          const items = [...allowances];
          items.splice(index, 1);
          updateSection('allowances', items);
        };
        
        const addManagement = () => {
          updateSection('management', [...management, { description: '', at_retail: '', at_cost: '' }]);
        };
        
        const updateManagement = (index, field, value) => {
          if (management[index]?.source_entry_id != null) return;
          const items = [...management];
          items[index] = { ...items[index], [field]: value };
          updateSection('management', items);
        };
        
        const removeManagement = (index) => {
          if (management[index]?.source_entry_id != null) return;
          const items = [...management];
          items.splice(index, 1);
          updateSection('management', items);
        };

        const adjustments = sections.adjustments || [];
        const addAdjustment = () => {
          updateSection('adjustments', [...adjustments, { description: '', at_retail: '' }]);
        };
        const updateAdjustment = (index, field, value) => {
          const items = [...adjustments];
          items[index] = { ...items[index], [field]: value };
          updateSection('adjustments', items);
        };
        // iter405 — Adjustments default to a negative number. We coerce
        // any bare positive figure to its negative on blur so the
        // user's typing stays unobstructed; the cursor never jumps,
        // and explicit positives can still be entered by typing them
        // and then re-positively-prefixing (a typed leading '+' is
        // also respected as a deliberate positive).
        const finaliseAdjustment = (index, value) => {
          if (typeof value !== 'string' || value === '' || value === '-') return;
          const trimmed = value.trim();
          // Explicit positive marker ('+5') is respected as positive.
          if (trimmed.startsWith('+')) {
            const items = [...adjustments];
            items[index] = { ...items[index], at_retail: trimmed.slice(1) };
            updateSection('adjustments', items);
            return;
          }
          const num = parseFloat(trimmed);
          if (!isNaN(num) && num > 0) {
            const items = [...adjustments];
            items[index] = { ...items[index], at_retail: `-${trimmed}` };
            updateSection('adjustments', items);
          }
        };
        const removeAdjustment = (index) => {
          const items = [...adjustments];
          items.splice(index, 1);
          updateSection('adjustments', items);
        };
        
        // Allowances table - Retail only (reused for Allowances + Adjustments;
        // button copy + testid derive from `title` so the Adjustments bucket
        // reads "Add Adjustment" instead of "Add Allowance").
        const renderAllowancesTable = (items, addFn, updateFn, removeFn, subtotal, title, icon, onBlurFn, options = [], manageCategory) => {
          const singular = title.endsWith('s') ? title.slice(0, -1) : title;
          const buttonLabel = `Add ${singular}`;
          const testIdSuffix = title.toLowerCase().replace(/s$/, '');
          return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium flex items-center gap-2">
                {icon}
                {title}
              </h4>
              <div className="flex items-center gap-3">
                {manageCategory && (
                  <button
                    type="button"
                    onClick={() => setManageListCategory(manageCategory)}
                    className="text-xs text-blue-600 hover:underline"
                    data-testid={`manage-${testIdSuffix}-list-link`}
                  >
                    Manage list
                  </button>
                )}
                {canEdit && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addFn}
                    data-testid={`add-${testIdSuffix}-btn`}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {buttonLabel}
                  </Button>
                )}
              </div>
            </div>
            
            {items.length === 0 ? (
              <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed">
                <p className="text-muted-foreground">No {title.toLowerCase()} added</p>
                {canEdit && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addFn}
                    className="mt-3"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add First
                  </Button>
                )}
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Description</th>
                      <th className="px-3 py-2 text-left font-medium w-40">Retail Selling Price £</th>
                      {canEdit && <th className="px-3 py-2 w-10"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      const isImported = item.source_entry_id != null;
                      return (
                      <tr key={idx} className={`border-t ${isImported ? 'bg-slate-50' : ''}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <SearchableSelect
                              options={options}
                              value={item.description || ''}
                              onValueChange={(val) => updateFn(idx, 'description', val)}
                              disabled={!canEdit || isImported}
                              data-testid={`${testIdSuffix}-${idx}-desc`}
                            />
                            {isImported && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 shrink-0" data-testid={`${testIdSuffix}-${idx}-locked-badge`}>
                                <Lock className="h-3 w-3" /> From Stocktake
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground text-xs">£</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.at_retail || ''}
                              onChange={(e) => updateFn(idx, 'at_retail', e.target.value)}
                              onBlur={(e) => { if (onBlurFn) onBlurFn(idx, e.target.value); }}
                              disabled={!canEdit || isImported}
                              placeholder="0.00"
                              className="h-9"
                              data-testid={`${testIdSuffix}-${idx}-retail`}
                            />
                          </div>
                        </td>
                        {canEdit && (
                          <td className="px-3 py-2">
                            {!isImported && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFn(idx)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                data-testid={`${testIdSuffix}-${idx}-delete`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-50 font-medium">
                    <tr className="border-t">
                      <td className="px-3 py-2 text-right">Subtotal:</td>
                      <td className="px-3 py-2">
                        <span data-testid={`${testIdSuffix}-subtotal-retail`}>£{subtotal.retail.toFixed(2)}</span>
                      </td>
                      {canEdit && <td></td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
          );
        };
        
        // Management table - Both Retail and Cost
        const renderManagementTable = (items, addFn, updateFn, removeFn, subtotal, title, icon, options = [], manageCategory) => (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium flex items-center gap-2">
                {icon}
                {title}
              </h4>
              <div className="flex items-center gap-3">
                {manageCategory && (
                  <button
                    type="button"
                    onClick={() => setManageListCategory(manageCategory)}
                    className="text-xs text-blue-600 hover:underline"
                    data-testid="manage-management-list-link"
                  >
                    Manage list
                  </button>
                )}
                {canEdit && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addFn}
                    data-testid="add-management-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Management Item
                  </Button>
                )}
              </div>
            </div>
            
            {items.length === 0 ? (
              <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed">
                <p className="text-muted-foreground">No management items added</p>
                {canEdit && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addFn}
                    className="mt-3"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add First
                  </Button>
                )}
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Description</th>
                      <th className="px-3 py-2 text-left font-medium w-36">At Retail £</th>
                      <th className="px-3 py-2 text-left font-medium w-36">At Cost £</th>
                      {canEdit && <th className="px-3 py-2 w-10"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      const isImported = item.source_entry_id != null;
                      return (
                      <tr key={idx} className={`border-t ${isImported ? 'bg-slate-50' : ''}`}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <SearchableSelect
                              options={options}
                              value={item.description || ''}
                              onValueChange={(val) => updateFn(idx, 'description', val)}
                              disabled={!canEdit || isImported}
                              data-testid={`management-${idx}-desc`}
                            />
                            {isImported && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 shrink-0" data-testid={`management-${idx}-locked-badge`}>
                                <Lock className="h-3 w-3" /> From Stocktake
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground text-xs">£</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.at_retail || ''}
                              onChange={(e) => updateFn(idx, 'at_retail', e.target.value)}
                              disabled={!canEdit || isImported}
                              placeholder="0.00"
                              className="h-9"
                              data-testid={`management-${idx}-retail`}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground text-xs">£</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.at_cost || ''}
                              onChange={(e) => updateFn(idx, 'at_cost', e.target.value)}
                              disabled={!canEdit || isImported}
                              placeholder="0.00"
                              className="h-9"
                              data-testid={`management-${idx}-cost`}
                            />
                          </div>
                        </td>
                        {canEdit && (
                          <td className="px-3 py-2">
                            {!isImported && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFn(idx)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                data-testid={`management-${idx}-delete`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-50 font-medium">
                    <tr className="border-t">
                      <td className="px-3 py-2 text-right">Subtotal:</td>
                      <td className="px-3 py-2">
                        <span data-testid="management-subtotal-retail">£{subtotal.retail.toFixed(2)}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span data-testid="management-subtotal-cost">£{subtotal.cost.toFixed(2)}</span>
                      </td>
                      {canEdit && <td></td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        );
        
        return (
          <div className="space-y-8">
            <p className="text-sm text-muted-foreground">
              Add allowances (at Retail Selling Price) and management items that affect the final figures.
            </p>

            {/* Visible, fail-closed carry-forward: every source Allowance
                line that has no verified category crosswalk is still
                brought into the report — exact signed amount, stable
                source-row identity, fully locked — rather than left for
                the auditor to re-type (which risked duplication). No
                category is ever guessed. */}
            {allowancesUncategorised.length > 0 && (
              <div className="rounded-lg border border-slate-300 bg-slate-50 overflow-hidden" data-testid="allowances-uncategorised-section">
                <div className="px-4 py-2.5 bg-slate-100 border-b border-slate-200 flex items-center gap-2">
                  <Lock className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-700">Uncategorised (from Stocktake)</span>
                  <span className="text-xs text-slate-500">
                    — {allowancesUncategorised.length} line(s), no approved category could be verified; carried
                    forward unchanged, nothing guessed
                  </span>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {allowancesUncategorised.map((row, i) => (
                      <tr key={row.source_entry_id ?? i} className="border-t border-slate-200" data-testid={`uncategorised-allowance-row-${i}`}>
                        <td className="px-4 py-2 text-slate-700">{row.description || '(no description)'}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-slate-700 w-40">
                          £{parseFloat(row.at_retail || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-2 w-28 text-right">
                          <span className="inline-flex items-center gap-1 text-xs font-normal text-slate-500" title="Imported verbatim from the Stocktake session — amount and description are server-protected">
                            <Lock className="h-3 w-3" /> Locked
                          </span>
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-slate-300 bg-slate-100/60 font-medium">
                      <td className="px-4 py-2 text-slate-600">Subtotal</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-700" data-testid="uncategorised-allowance-subtotal">
                        £{allowancesUncategorised.reduce((sum, r) => sum + (parseFloat(r.at_retail) || 0), 0)
                          .toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {renderAllowancesTable(
              allowances,
              addAllowance,
              updateAllowance,
              removeAllowance,
              allowancesSubtotal,
              'Allowances',
              <ListChecks className="h-4 w-4 text-blue-600" />,
              undefined,
              allowanceOptions,
              'allowance'
            )}
            
            <div className="border-t pt-6">
              {renderManagementTable(
                management,
                addManagement,
                updateManagement,
                removeManagement,
                managementSubtotal,
                'Management',
                <Briefcase className="h-4 w-4 text-purple-600" />,
                managementOptions,
                'management'
              )}
            </div>

            <div className="border-t pt-6">
              {renderAllowancesTable(
                adjustments,
                addAdjustment,
                updateAdjustment,
                removeAdjustment,
                adjustmentsSubtotal,
                'Adjustments',
                <ListChecks className="h-4 w-4 text-amber-600" />,
                finaliseAdjustment,  // iter405 — only Adjustments auto-negate; Allowances stay positive.
                adjustmentOptions,
                'adjustment'
              )}
            </div>
          </div>
        );

      case 'abv':
        // Step 5: ABV Check
        const abvChecks = sections.abv_checks || [];
        
        const addAbvItem = () => {
          updateSection('abv_checks', [...abvChecks, {
            product: '',
            temperature: '',
            density: '',
            measured_abv: '',
            pass_fail: null,
            action_taken: ''
          }]);
        };
        
        const updateAbvItem = (index, field, value) => {
          const items = [...abvChecks];
          items[index] = { ...items[index], [field]: value };
          updateSection('abv_checks', items);
        };
        
        const removeAbvItem = (index) => {
          const items = [...abvChecks];
          items.splice(index, 1);
          updateSection('abv_checks', items);
        };
        
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Record ABV verification results for products tested during the audit.
                </p>
              </div>
              {canEdit && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addAbvItem}
                  data-testid="add-abv-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Product
                </Button>
              )}
            </div>
            
            {abvChecks.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-6 text-center bg-slate-50 rounded">
                No ABV checks recorded. Click "Add Product" to begin.
              </p>
            ) : (
              <div className="space-y-3">
                {abvChecks.map((item, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-white space-y-2" data-testid={`abv-item-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Product {idx + 1}</span>
                      {canEdit && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAbvItem(idx)}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          data-testid={`abv-${idx}-delete`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                      <div className="col-span-2 md:col-span-1">
                        <Label className="text-xs">Product</Label>
                        <Input
                          value={item.product || ''}
                          onChange={(e) => updateAbvItem(idx, 'product', e.target.value)}
                          disabled={!canEdit}
                          placeholder="e.g. Smirnoff Vodka"
                          className="h-8 text-sm"
                          data-testid={`abv-${idx}-product`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Temp (°C)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={item.temperature || ''}
                          onChange={(e) => updateAbvItem(idx, 'temperature', e.target.value)}
                          disabled={!canEdit}
                          placeholder="20.0"
                          className="h-8 text-sm"
                          data-testid={`abv-${idx}-temperature`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Density (g/ml)</Label>
                        <Input
                          type="number"
                          step="0.001"
                          value={item.density || ''}
                          onChange={(e) => updateAbvItem(idx, 'density', e.target.value)}
                          disabled={!canEdit}
                          placeholder="0.950"
                          className="h-8 text-sm"
                          data-testid={`abv-${idx}-density`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Measured ABV (%)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={item.measured_abv || ''}
                          onChange={(e) => updateAbvItem(idx, 'measured_abv', e.target.value)}
                          disabled={!canEdit}
                          placeholder="40.0"
                          className="h-8 text-sm"
                          data-testid={`abv-${idx}-abv`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Result</Label>
                        <div className="flex gap-1 mt-1">
                          <Button
                            type="button"
                            size="sm"
                            variant={item.pass_fail === true ? 'default' : 'outline'}
                            className={`h-8 flex-1 text-xs ${item.pass_fail === true ? 'bg-green-600 hover:bg-green-700' : ''}`}
                            onClick={() => canEdit && updateAbvItem(idx, 'pass_fail', true)}
                            disabled={!canEdit}
                            data-testid={`abv-${idx}-pass`}
                          >
                            Pass
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={item.pass_fail === false ? 'default' : 'outline'}
                            className={`h-8 flex-1 text-xs ${item.pass_fail === false ? 'bg-red-600 hover:bg-red-700' : ''}`}
                            onClick={() => canEdit && updateAbvItem(idx, 'pass_fail', false)}
                            disabled={!canEdit}
                            data-testid={`abv-${idx}-fail`}
                          >
                            Fail
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Action Taken / Recommendation (optional)</Label>
                      <Input
                        value={item.action_taken || ''}
                        onChange={(e) => updateAbvItem(idx, 'action_taken', e.target.value)}
                        disabled={!canEdit}
                        placeholder="e.g. Product removed from sale, supplier notified"
                        className="h-8 text-sm"
                        data-testid={`abv-${idx}-action`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'food_allowances': {
        // Food Allowances — single amount + retail/cost toggle per line
        const foodAllowances = sections.food_allowances || [];
        
        const addFoodAllowance = () => {
          updateSection('food_allowances', [...foodAllowances, { description: '', amount: '', value_type: '' }]);
        };
        
        const updateFoodAllowance = (index, field, value) => {
          const items = [...foodAllowances];
          items[index] = { ...items[index], [field]: value };
          updateSection('food_allowances', items);
        };
        
        const removeFoodAllowance = (index) => {
          const items = [...foodAllowances];
          items.splice(index, 1);
          updateSection('food_allowances', items);
        };
        
        const foodAllowancesTotal = foodAllowances.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Add allowances for the food audit. Each line requires an amount and whether it is at Retail or Cost value.
                </p>
              </div>
              {canEdit && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addFoodAllowance}
                  data-testid="add-food-allowance-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Allowance
                </Button>
              )}
            </div>
            
            {foodAllowances.length === 0 ? (
              <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed">
                <p className="text-muted-foreground">No allowances added</p>
                {canEdit && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addFoodAllowance}
                    className="mt-3"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add First
                  </Button>
                )}
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Description</th>
                      <th className="px-3 py-2 text-left font-medium w-36">Retail / Cost</th>
                      <th className="px-3 py-2 text-left font-medium w-36">Amount ({currencySymbol})</th>
                      {canEdit && <th className="px-3 py-2 w-10"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {foodAllowances.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">
                          <Input
                            value={item.description || ''}
                            onChange={(e) => updateFoodAllowance(idx, 'description', e.target.value)}
                            disabled={!canEdit}
                            placeholder="e.g., Staff meals, Wastage"
                            className="h-9"
                            data-testid={`food-allowance-${idx}-desc`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant={item.value_type === 'retail' ? 'default' : 'outline'}
                              className={`h-8 flex-1 text-xs ${item.value_type === 'retail' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                              onClick={() => canEdit && updateFoodAllowance(idx, 'value_type', 'retail')}
                              disabled={!canEdit}
                              data-testid={`food-allowance-${idx}-retail`}
                            >
                              Retail
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={item.value_type === 'cost' ? 'default' : 'outline'}
                              className={`h-8 flex-1 text-xs ${item.value_type === 'cost' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                              onClick={() => canEdit && updateFoodAllowance(idx, 'value_type', 'cost')}
                              disabled={!canEdit}
                              data-testid={`food-allowance-${idx}-cost`}
                            >
                              Cost
                            </Button>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground text-xs">{currencySymbol}</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.amount || ''}
                              onChange={(e) => updateFoodAllowance(idx, 'amount', e.target.value)}
                              disabled={!canEdit}
                              placeholder="0.00"
                              className="h-9"
                              data-testid={`food-allowance-${idx}-amount`}
                            />
                          </div>
                        </td>
                        {canEdit && (
                          <td className="px-3 py-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFoodAllowance(idx)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              data-testid={`food-allowance-${idx}-delete`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-medium">
                    <tr className="border-t">
                      <td className="px-3 py-2 text-right" colSpan={2}>Total:</td>
                      <td className="px-3 py-2">
                        <span data-testid="food-allowance-total">{currencySymbol}{foodAllowancesTotal.toFixed(2)}</span>
                      </td>
                      {canEdit && <td></td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        );
      }


      case 'observations':
        // Step 6: Observations - General Audit Comments and Actions Agreed only
        const observations = sections.observations || {};
        return (
          <div className="space-y-6">
            {/* Guidance note for auditors */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800" data-testid="auditor-guidance-note">
              <p className="font-medium mb-1">Note for Auditors</p>
              <p>Please ensure that observations take a balanced view of client performance. It is as important to highlight positive actions and achievements as it is to highlight areas for improvement.</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="general-comments" className="text-sm font-medium">
                  General Audit Comments
                </Label>
                <Textarea
                  id="general-comments"
                  value={observations.key || ''}
                  onChange={(e) => updateSection('observations', { ...observations, key: e.target.value })}
                  disabled={!canEdit}
                  placeholder="Enter general observations and comments from this audit..."
                  className="mt-1 min-h-[150px]"
                  data-testid="step5-general-comments"
                />
              </div>
              
              <div>
                <Label htmlFor="actions-agreed" className="text-sm font-medium">
                  Actions Agreed
                </Label>
                <Textarea
                  id="actions-agreed"
                  value={observations.actions || ''}
                  onChange={(e) => updateSection('observations', { ...observations, actions: e.target.value })}
                  disabled={!canEdit}
                  placeholder="List the agreed actions to address issues and improve operations..."
                  className="mt-1 min-h-[150px]"
                  data-testid="step5-actions-agreed"
                />
              </div>
            </div>
          </div>
        );

      case 'schedule':
        // Step 7: Schedule Next Audit - READ ONLY
        // Shows the next scheduled audit date from the job schedule, or TBC
        return (
          <div className="space-y-4">
            {report.next_audit_job_id ? (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-800">Next {scopeInfo.label} Audit Scheduled</AlertTitle>
                <AlertDescription className="text-green-700">
                  <div className="mt-2 space-y-1">
                    {report.next_audit_start_date && (
                      <p><strong>Start:</strong> {formatDate(report.next_audit_start_date)}</p>
                    )}
                    {report.next_audit_end_date && (
                      <p><strong>End:</strong> {formatDate(report.next_audit_end_date)}</p>
                    )}
                    <Link 
                      to={`/jobs/${report.next_audit_job_id}`}
                      className="inline-flex items-center text-green-700 hover:text-green-900 underline"
                    >
                      View Job #{report.next_audit_job_id}
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Link>
                  </div>
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="border-amber-200 bg-amber-50">
                <HelpCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">To Be Confirmed</AlertTitle>
                <AlertDescription className="text-amber-700">
                  The next audit date has not been scheduled yet. It will appear here automatically once a future job is scheduled for this site.
                </AlertDescription>
              </Alert>
            )}
            
            <div className="bg-slate-100 p-4 rounded-md text-sm">
              <p className="text-muted-foreground">
                The next audit date is determined by the job schedule. To schedule the next audit, 
                create a new job entry in the <Link to="/schedule" className="text-blue-600 hover:underline">Schedule</Link> page.
              </p>
            </div>
          </div>
        );

      case 'review':
        // Step 8: Review Preview - uses shared ReportPreviewContent component (single source of truth)
        return (
          <>
          <ReportPreviewContent
            report={report}
            sectionsJson={sections}
            settings={settings}
            showExportPdf={true}
            onExportPdf={handleExportPdf}
            isExporting={exportingPdf}
            showActions={false}
            canEdit={canEdit}
            capturedMetrics={capturedMetrics}
            metricsTemplate={metricsTemplate}
          />
          {summaryLinkStatus?.linked && <StockAuditAttachmentPanel reportId={report.id} canEdit={canEdit && report?.status === 'DRAFT'} />}
          </>
        );

      default:
        return null;
    }
  };

  // =============================================
  // MAIN RENDER
  // =============================================

  return (
    <div className="p-6 max-w-5xl mx-auto summary-wizard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={async () => {
            // If report was just created with no data, delete it silently
            if (report && report.is_editable && !hasAnyData()) {
              try { await summaryReportsService.delete(report.id); } catch (_) {}
            }
            navigate(-1);
          }} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <span className={scopeInfo.color}>{scopeInfo.icon}</span>
              {scopeInfo.label} Stock Audit Report
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {report.parent_client_id ? `${report.client_name} • ${report.site_name}` : report.site_name}
              {report.audit_date && stockPeriodDays > 0 && (() => {
                // 2026-04-24 iter322 — Prefer server-hardened previous_audit_date.
                const MS_PER_DAY = 86400000;
                let startIso = report.previous_audit_date;
                if (!startIso) {
                  const endMs = new Date(report.audit_date + 'T00:00:00Z').getTime();
                  startIso = new Date(endMs - stockPeriodDays * MS_PER_DAY)
                    .toISOString().split('T')[0];
                }
                return <span className="ml-2 text-slate-500">— Period: {formatDate(startIso)} to {formatDate(report.audit_date)}</span>;
              })()}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-sm px-3 py-1 ${scopeInfo.color}`}>
            {scopeInfo.label}
          </Badge>
          <Badge 
            variant={isPublished ? "default" : "secondary"}
            className={`text-sm px-3 py-1 ${
              isPublished 
                ? 'bg-green-100 text-green-800' 
                : isPendingApproval
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-slate-100 text-slate-800'
            }`}
          >
            {isPublished ? (
              <><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Published</>
            ) : isPendingApproval ? (
              <><Send className="h-3.5 w-3.5 mr-1" />Pending Approval</>
            ) : (
              <><Edit3 className="h-3.5 w-3.5 mr-1" />Draft</>
            )}
          </Badge>
        </div>
      </div>

      {/* Published Notice */}
      {isPublished && (
        <Alert className="mb-6 border-green-200 bg-green-50 print:hidden">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">Published Stock Audit Report</AlertTitle>
          <AlertDescription className="text-green-700 flex items-center justify-between">
            <span>
              Published on {formatDateTime(report.published_at)} by {report.published_by_name}. 
              This report is now available in the repository and is read-only.
            </span>
            <div className="flex items-center gap-2 ml-4 shrink-0">
              {/* Only show the AP route when the client/group has AP enabled.
                  Without this guard the button appears for all published
                  reports and leads to a confusing "No AP exists / Create"
                  prompt when AP is actually OFF for the client. */}
              {report?.ap_eligible && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/summary-reports/${report.id}/action-plan`)}
                className="text-blue-600 border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                data-testid="open-action-plan-btn"
              >
                Action Plan
              </Button>
              )}
              {isAdminOrScheduler && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowUnpublishDialog(true)}
                  className="text-amber-600 border-amber-300 hover:bg-amber-50 hover:text-amber-700"
                  data-testid="unpublish-btn"
                >
                  <EyeOff className="h-4 w-4 mr-1" />
                  Unpublish
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Pending Approval Notice */}
      {isPendingApproval && (
        <Alert className="mb-6 border-amber-200 bg-amber-50 print:hidden">
          <Send className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Awaiting Scheduler Approval</AlertTitle>
          <AlertDescription className="text-amber-700">
            {isAuditorOnlyRole
              ? 'This report has been submitted for approval. A scheduler will review and approve it.'
              : 'This report is pending approval. You can edit, approve, or return it to the auditor using the buttons below.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Draft Recovery Notice (2026-09) — warns, does not block editing */}
      {recentEditNotice && (
        <Alert className="mb-6 print:hidden border-sky-200 bg-sky-50" data-testid="draft-recovery-notice">
          <Users className="h-4 w-4 text-sky-600" />
          <AlertTitle className="flex items-center justify-between">
            <span>Recently edited by {recentEditNotice.name}</span>
            <button
              type="button"
              onClick={() => setRecentEditNotice(null)}
              className="text-sky-600 hover:text-sky-800"
              data-testid="draft-recovery-notice-dismiss"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>
              {recentEditNotice.name} saved changes to this draft {recentEditNotice.secsAgo < 5 ? 'just now' : `${recentEditNotice.secsAgo}s ago`}.
              You can keep editing, but refresh first to make sure you're building on their latest changes.
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="shrink-0 border-sky-300"
              onClick={() => window.location.reload()}
              data-testid="draft-recovery-notice-refresh-btn"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Unsaved Changes / Autosave Status */}
      {canEdit && (hasUnsavedChanges || autosaveState !== 'idle') && (
        <Alert
          className={`mb-6 print:hidden transition-opacity duration-500 ${autosaveFadingOut ? 'opacity-0' : 'opacity-100'} ${
            autosaveState === 'saving'
              ? 'border-blue-200 bg-blue-50'
              : autosaveState === 'saved' && !hasUnsavedChanges
                ? 'border-emerald-200 bg-emerald-50'
                : autosaveState === 'error'
                  ? 'border-red-200 bg-red-50'
                  : 'border-amber-200 bg-amber-50'
          }`}
          data-testid="autosave-status-alert"
        >
          {autosaveState === 'saved' && !hasUnsavedChanges ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" data-testid="autosave-saved-checkmark" />
          ) : (
            <AlertTriangle
              className={`h-4 w-4 ${
                autosaveState === 'saving'
                  ? 'text-blue-600'
                  : autosaveState === 'error'
                    ? 'text-red-600'
                    : 'text-amber-600'
            }`}
            />
          )}
          <AlertTitle
            className={
              autosaveState === 'saving'
                ? 'text-blue-800'
                : autosaveState === 'saved' && !hasUnsavedChanges
                  ? 'text-emerald-800'
                  : autosaveState === 'error'
                    ? 'text-red-800'
                    : 'text-amber-800'
            }
          >
            {autosaveState === 'saving'
              ? 'Saving draft…'
              : autosaveState === 'saved' && !hasUnsavedChanges
                ? 'Draft autosaved'
                : autosaveState === 'error'
                  ? 'Autosave failed — please save manually'
                  : 'Unsaved Changes'}
          </AlertTitle>
          <AlertDescription
            className={
              autosaveState === 'saving'
                ? 'text-blue-700'
                : autosaveState === 'saved' && !hasUnsavedChanges
                  ? 'text-emerald-700'
                  : autosaveState === 'error'
                    ? 'text-red-700'
                    : 'text-amber-700'
            }
          >
            {autosaveState === 'saving'
              ? 'Persisting your edits to the server…'
              : autosaveState === 'saved' && !hasUnsavedChanges
                ? 'All recent edits have been saved.'
                : autosaveState === 'error'
                  ? 'The autosave attempt failed. Use "Save & Exit" or save from the review step to retry.'
                  : 'Your changes are autosaved every few seconds and whenever you change step.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Wizard Progress (hidden in print) */}
      <div className="mb-8 print:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Step {currentStep} of {WIZARD_STEPS.length}</span>
          <span className="text-sm text-muted-foreground">{WIZARD_STEPS[currentStep - 1].name}</span>
        </div>
        <div className="flex gap-1">
          {WIZARD_STEPS.map((step) => (
            <button
              key={step.id}
              onClick={() => handleStepChange(step.id)}
              className={`flex-1 h-2 rounded-full transition-colors ${
                step.id === currentStep 
                  ? 'bg-blue-600' 
                  : step.id < currentStep 
                    ? 'bg-blue-300' 
                    : 'bg-slate-200'
              }`}
              title={step.name}
              data-testid={`step-indicator-${step.id}`}
            />
          ))}
        </div>
        <div className="flex justify-between mt-2 text-xs text-muted-foreground">
          {WIZARD_STEPS.map((step) => (
            <button
              key={step.id}
              onClick={() => handleStepChange(step.id)}
              className={`flex flex-col items-center gap-1 w-24 ${
                step.id === currentStep ? 'text-blue-600 font-medium' : ''
              }`}
            >
              <step.icon className="h-4 w-4" />
              <span className="hidden sm:inline text-center leading-tight">{step.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── P3 Scope B — active link + mismatch banner (read-only). ──────
           Distinct from the P1 prefill preview below: this reflects an
           actual persisted link (once the CTO-gated migration exists),
           not a suggestion. Shown only when summaryLinkStatus.linked. */}
      {summaryLinkStatus && summaryLinkStatus.linked && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 print:hidden ${
            summaryLinkStatus.mismatch ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'
          }`}
          data-testid="summary-link-status-banner"
        >
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <Link2 className="h-3.5 w-3.5 shrink-0" />
            <span className="font-semibold">
              Linked to Stocktake {summaryLinkStatus.session_reference ? `#${summaryLinkStatus.session_reference}` : `session ${summaryLinkStatus.session_id}`} v{summaryLinkStatus.snapshot_version}
            </span>
            {summaryLinkStatus.locked_fields?.length > 0 && (
              <span className="text-slate-500">· {summaryLinkStatus.locked_fields.length} field(s) locked from source</span>
            )}
            {summaryLinkStatus.source_status === 'completed' && (
              <Badge className="text-[9px] bg-indigo-100 text-indigo-800 border-indigo-300" data-testid="summary-link-draft-badge">
                Draft source — Stocktake not yet published
              </Badge>
            )}
          </div>
          {summaryLinkStatus.mismatch && (
            <div className="mt-1 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[11px] text-amber-800" data-testid="summary-link-mismatch-note">
                {summaryLinkStatus.mismatch_reason || 'The source session has changed since this Summary was launched.'} Nothing was auto-rewritten.
              </p>
              {summaryLinkStatus.can_refresh && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={refreshingSource}
                  onClick={handleRefreshSource}
                  className="h-7 text-xs border-amber-400 text-amber-800 hover:bg-amber-100"
                  data-testid="summary-link-refresh-source-btn"
                >
                  {refreshingSource ? 'Refreshing…' : 'Refresh from latest calculation'}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Prefill preview — only shown before a Summary is actually linked to
          a Stocktake. Once linked (banner above), the locked wizard fields
          already carry this information with their own source/lock cue, so
          showing it twice would be redundant. */}
      {stocktakePrefill && !(summaryLinkStatus && summaryLinkStatus.linked) && (
        <div
          className="mb-4 rounded-lg border border-sky-200 bg-sky-50/60 print:hidden"
          data-testid="stocktake-prefill-banner"
        >
          {/* Header row */}
          <button
            type="button"
            onClick={() => setPrefillExpanded(x => !x)}
            className="w-full flex items-center justify-between px-4 py-3 text-left"
            data-testid="prefill-banner-toggle"
          >
            <span className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-sky-100 text-sky-600 text-[10px] font-bold ring-1 ring-sky-200">S</span>
              <span className="text-sm font-semibold text-sky-900">Prefill from Stocktake</span>
              <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-600 ring-1 ring-sky-200">Read-only preview</span>
              {stocktakePrefill.snapshot_meta?.session_reference && (
                <span className="text-xs text-sky-600 font-mono hidden sm:inline">
                  Ref: {stocktakePrefill.snapshot_meta.session_reference}
                </span>
              )}
            </span>
            <span className="text-sky-500 text-xs">{prefillExpanded ? '▲ Hide' : '▼ Show'}</span>
          </button>

          {prefillExpanded && (
            <div className="border-t border-sky-200 px-4 pb-4 pt-3 space-y-4">
              {/* Snapshot provenance — kept for audit (provenance is
                  available on demand; this panel only opens when the
                  auditor clicks "Show detail"). */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-sky-700">
                {stocktakePrefill.snapshot_meta?.session_date && (
                  <span>Session date: <strong>{stocktakePrefill.snapshot_meta.session_date}</strong></span>
                )}
                {stocktakePrefill.snapshot_meta?.period_from && (
                  <span>Period: <strong>{stocktakePrefill.snapshot_meta.period_from}</strong> – <strong>{stocktakePrefill.snapshot_meta.period_to}</strong></span>
                )}
                {stocktakePrefill.snapshot_meta?.snapshot_version != null && (
                  <span>Snapshot v{stocktakePrefill.snapshot_meta.snapshot_version}</span>
                )}
                {stocktakePrefill.snapshot_meta?.calculated_at && (
                  <span>Finalised: {new Date(stocktakePrefill.snapshot_meta.calculated_at).toLocaleString()}</span>
                )}
              </div>

              {/* Core metrics grid */}
              {stocktakePrefill.metrics && (() => {
                const m = stocktakePrefill.metrics;
                const fmt1dp = (v) => v != null ? parseFloat(v).toFixed(1) : '—';
                const fmtCcy = (v) => v != null ? `£${parseFloat(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
                const fmtPct = (v) => v != null ? `${parseFloat(v).toFixed(1)}%` : '—';
                return (
                  <div>
                    <p className="text-[11px] font-semibold text-slate-600 mb-1.5">Period metrics</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { label: 'Sales (incl. VAT)', value: fmtCcy(m.sales_for_period) },
                        { label: 'Stockholding', value: fmtCcy(m.stockholding_value) },
                        { label: 'GP %', value: fmtPct(m.gp_percent) },
                        { label: 'Yield %', value: fmtPct(m.yield_percent) },
                        { label: 'Stocktake result', value: fmtCcy(m.stocktake_result_value) },
                        { label: 'Result %', value: fmtPct(m.stocktake_result_percent) },
                        m.wastage_value != null
                          ? { label: 'Wastage', value: fmtCcy(m.wastage_value) }
                          : null,
                        m.stockholding_days != null
                          ? { label: 'Stockholding days', value: fmt1dp(m.stockholding_days) }
                          : null,
                      ].filter(Boolean).map(({ label, value }) => (
                        <div key={label} className="rounded bg-white border border-sky-100 px-2.5 py-2">
                          <p className="text-[10px] text-slate-500">{label}</p>
                          <p className="text-sm font-semibold text-slate-800 tabular-nums">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Allowances imported from the Stocktake session */}
              {stocktakePrefill.allowances?.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold text-slate-600 mb-1.5">
                    Allowances{' '}
                    {stocktakePrefill.allowances.some(a => a.provenance === 'unresolved') && (
                      <span className="ml-1 text-amber-600 font-normal" data-testid="allowances-unresolved-note">
                        — some lines could not be matched automatically
                      </span>
                    )}
                  </p>
                  <div className="space-y-1">
                    {stocktakePrefill.allowances.map((a, i) => (
                      <div
                        key={i}
                        className={`flex items-center justify-between rounded px-2.5 py-1.5 text-xs border ${
                          a.provenance === 'unresolved'
                            ? 'border-amber-200 bg-amber-50/40'
                            : 'border-sky-100 bg-white'
                        }`}
                        data-testid={`prefill-allowance-${i}`}
                      >
                        <span className="font-medium text-slate-700 flex-1 mr-3">{a.description || '(no description)'}</span>
                        <span className="tabular-nums text-slate-600 mr-3">
                          {a.at_retail != null ? `£${parseFloat(a.at_retail).toFixed(2)}` : '—'}
                        </span>
                        {a.provenance === 'unresolved' ? (
                          <span className="text-[10px] text-amber-600 font-medium whitespace-nowrap">Unresolved</span>
                        ) : (
                          <span className="text-[10px] text-slate-400">Grp {a.group_code}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Year-to-date figures */}
              {stocktakePrefill.ytd && stocktakePrefill.ytd_coverage?.eligible && (
                <div>
                  <p className="text-[11px] font-semibold text-slate-600 mb-1.5">Year-to-date figures</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                      { label: 'YTD Sales', value: stocktakePrefill.ytd.ytd_sales_amount },
                      { label: 'YTD GP %', value: stocktakePrefill.ytd.ytd_gross_profit_percent, pct: true },
                      { label: 'YTD Result £', value: stocktakePrefill.ytd.ytd_result_amount },
                      { label: 'YTD Result %', value: stocktakePrefill.ytd.ytd_result_percent, pct: true },
                      { label: 'YTD Yield %', value: stocktakePrefill.ytd.ytd_yield_percent, pct: true },
                    ].filter(f => f.value != null).map(({ label, value, pct }) => (
                      <div key={label} className="rounded bg-white border border-sky-100 px-2.5 py-2">
                        <p className="text-[10px] text-slate-500">{label}</p>
                        <p className="text-sm font-semibold text-slate-800 tabular-nums">
                          {pct
                            ? `${parseFloat(value).toFixed(1)}%`
                            : `£${parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`
                          }
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* YTD not eligible */}
              {stocktakePrefill.ytd_coverage && !stocktakePrefill.ytd_coverage.eligible && (
                <p className="text-[11px] text-amber-700 bg-amber-50/40 border border-amber-200 rounded px-3 py-2" data-testid="ytd-not-eligible-note">
                  <strong>Year-to-date:</strong> {stocktakePrefill.ytd_coverage.reason}
                </p>
              )}

              {/* Blank-field notes — explain why these need manual entry */}
              <div className="space-y-1 text-[11px] text-slate-500">
                <p data-testid="prefill-d3-note"><strong className="text-slate-700">Adjustments:</strong> {stocktakePrefill.adjustments_note}</p>
                <p data-testid="prefill-d7-note"><strong className="text-slate-700">Variances:</strong> {stocktakePrefill.variances_note}</p>
                <p data-testid="prefill-d8-note" className="text-sky-700">{stocktakePrefill.nearest_date_note}</p>
              </div>

              <p className="text-[10px] text-slate-400 italic">
                This panel mirrors the values already locked into this report from the Stocktake session — it is for reference and audit only.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Step Content */}
      <Card className="mb-6 print:shadow-none print:border-0">
        <CardHeader className="print:hidden">
          <CardTitle className="flex items-center gap-2">
            {React.createElement(WIZARD_STEPS[currentStep - 1].icon, { className: "h-5 w-5" })}
            {WIZARD_STEPS[currentStep - 1].name}
          </CardTitle>
        </CardHeader>
        <CardContent className="print:p-0">
          {renderStepContent()}
        </CardContent>
      </Card>

      {/* Navigation & Action Buttons — single consolidated bar (hidden in print) */}
      {/* D1 — AP Decision panel: shows on last step for editable reports WHERE the
              client/group has AP enabled (ap_eligible=true). Hiding it when AP is OFF
              prevents auditors saving a decision that would be silently discarded and
              removes the false impression that an Action Plan will be issued. */}
      {currentStep === WIZARD_STEPS.length && canEdit && report?.ap_eligible && (
        <div className="mb-4 p-4 border border-amber-200 bg-amber-50/40 rounded-lg print:hidden" data-testid="ap-decision-panel">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                Action Plan Decision
              </h4>
              <p className="text-xs text-amber-700 mt-0.5">
                Record your decision before publishing. Selecting "Issue" creates a <strong>draft</strong> Action Plan; notification emails are only sent when the auditor explicitly publishes the AP.
              </p>
              {/* Items 2/9: threshold basis inline — so auditor sees result context before deciding */}
              {(() => {
                const metrics = report?.sections?.find?.(s => s.section_type === 'metrics')?.content
                             || report?.metrics
                             || null;
                const resultPct  = metrics?.variance_pct ?? metrics?.result_pct ?? null;
                const threshLow  = report?.ap_threshold_low  ?? report?.effective_threshold_low  ?? null;
                const threshHigh = report?.ap_threshold_high ?? report?.effective_threshold_high ?? null;
                if (resultPct == null) return null;
                const pctLabel = `${(resultPct * 100).toFixed(2)}%`;
                let basisNote = null;
                if (threshLow != null && resultPct < threshLow)
                  basisNote = `${Math.abs((threshLow - resultPct) * 100).toFixed(2)}pp below threshold (${(threshLow*100).toFixed(2)}%)`;
                else if (threshHigh != null && resultPct > threshHigh)
                  basisNote = `${Math.abs((resultPct - threshHigh) * 100).toFixed(2)}pp above threshold (${(threshHigh*100).toFixed(2)}%)`;
                else if (threshLow != null || threshHigh != null)
                  basisNote = 'within threshold';
                return (
                  <p className="text-xs mt-1 font-medium text-amber-800" data-testid="ap-decision-threshold-basis">
                    Result: {pctLabel}{basisNote ? ` · ${basisNote}` : ''}
                  </p>
                );
              })()}
            </div>
            {(report?.ap_decision === apDecision) && report?.ap_decision && (
              <Badge variant="outline" className={
                report.ap_decision === 'issue' ? 'border-amber-500 text-amber-700 bg-amber-50' : 'border-slate-400 text-slate-600'
              }>
                {report.ap_decision === 'issue' ? 'AP: Issue (draft)' : 'AP: Suppress'}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={apDecision === 'issue' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setApDecision(apDecision === 'issue' ? '' : 'issue')}
              className={apDecision === 'issue' ? 'bg-amber-600 hover:bg-amber-700 text-white border-amber-600' : 'border-amber-400 text-amber-700 hover:bg-amber-50'}
              data-testid="ap-decision-issue-btn"
            >
              <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
              Issue Action Plan (draft)
            </Button>
            <Button
              variant={apDecision === 'suppress' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setApDecision(apDecision === 'suppress' ? '' : 'suppress')}
              className={apDecision === 'suppress' ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'text-slate-600 hover:bg-slate-50'}
              data-testid="ap-decision-suppress-btn"
            >
              <X className="h-3.5 w-3.5 mr-1.5" />
              Suppress
            </Button>
          </div>
          {apDecision && (
            <Input
              className="mt-2 h-8 text-sm border-amber-200"
              placeholder="Reason (optional)..."
              value={apDecisionReason}
              onChange={(e) => setApDecisionReason(e.target.value)}
              maxLength={500}
              data-testid="ap-decision-reason-input"
            />
          )}
          {/* Show Save button only when there's an unsaved change */}
          {(apDecision !== (report?.ap_decision || '') ||
            apDecisionReason !== (report?.ap_decision_reason || '')) && (
            <div className="flex items-center justify-end mt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleSaveApDecision}
                disabled={savingApDecision}
                className="border-amber-400 text-amber-700 hover:bg-amber-50"
                data-testid="ap-decision-save-btn"
              >
                {savingApDecision ? (
                  <div className="h-3.5 w-3.5 mr-1.5 animate-spin border-2 border-current border-t-transparent rounded-full" />
                ) : (
                  <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                )}
                Save Decision
              </Button>
            </div>
          )}
        </div>
      )}

      {/* D4 — Linked Action Plan banner (shown when AP decision is 'issue' and an AP exists) */}
      {report?.ap_decision === 'issue' && (
        <div className="mb-4 p-3 border border-amber-300 bg-amber-50 rounded-lg print:hidden flex items-center justify-between gap-3" data-testid="linked-ap-banner">
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
            <span>
              {linkedApLoading
                ? 'Loading linked Action Plan…'
                : linkedAp
                  ? `Action Plan #${linkedAp.reference || linkedAp.id} issued for this report.`
                  : 'Action Plan marked for issue — will be created on publication.'}
            </span>
          </div>
          {linkedAp && (
            <Button
              size="sm"
              variant="outline"
              className="border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0 text-xs"
              onClick={() => navigate(`/action-plans/${linkedAp.id}`)}
              data-testid="view-linked-ap-btn"
            >
              View Action Plan →
            </Button>
          )}
        </div>
      )}

      <div className="flex justify-between items-center print:hidden">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => handleStepChange(currentStep - 1)}
            disabled={currentStep === 1}
            data-testid="prev-step-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          {/* Delete — only for drafts, by authorised users */}
          {canDelete && canEdit && !isPendingApproval && (isAdminOrScheduler || (isAuditorRole && report?.created_by_user_id === user?.id)) && (
            <Button variant="outline" size="sm" onClick={() => setShowDeleteDialog(true)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-report-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          )}
          {/* Return to Auditor — only for schedulers on PENDING reports */}
          {isPendingApproval && isAdminOrScheduler && (
            <Button variant="outline" size="sm" onClick={() => setShowReturnDialog(true)}
              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" data-testid="return-to-auditor-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Return to Auditor
            </Button>
          )}
          {/* Return to Stocktake — only when this Summary has an active
              source link; takes the auditor back to the exact linked
              session, offering Save Draft/Stay if there are unsaved edits. */}
          {summaryLinkStatus?.linked && (
            <Button variant="outline" size="sm" onClick={handleReturnToStocktake}
              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" data-testid="return-to-stocktake-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Return to Stocktake
            </Button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="outline" onClick={handleSaveAndExit} disabled={saving} data-testid="save-exit-btn">
              {saving ? <div className="h-4 w-4 mr-2 animate-spin border-2 border-current border-t-transparent rounded-full" /> : <LogOut className="h-4 w-4 mr-2" />}
              Save & Exit
            </Button>
          )}
          
          {currentStep < WIZARD_STEPS.length ? (
            <Button
              onClick={() => handleStepChange(currentStep + 1)}
              data-testid="next-step-btn"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : canEdit ? (
            <>
              {showSubmitForApproval ? (
                <Button
                  onClick={handleSubmitForApproval}
                  disabled={submittingForApproval}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="submit-for-approval-btn"
                >
                  {submittingForApproval ? (
                    <div className="h-4 w-4 mr-2 animate-spin border-2 border-current border-t-transparent rounded-full" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Submit for Approval
                </Button>
              ) : isPendingApproval && isAuditorOnlyRole ? null
              : isPendingApproval && isAdminOrScheduler ? (
                <Button
                  onClick={handleApprove}
                  disabled={publishing}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="approve-btn"
                >
                  {publishing ? (
                    <div className="h-4 w-4 mr-2 animate-spin border-2 border-current border-t-transparent rounded-full" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Approve &amp; Publish
                </Button>
              ) : !isPendingApproval ? (
                <Button
                  onClick={() => setShowPublishDialog(true)}
                  disabled={publishing}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="publish-btn"
                >
                  {publishing ? (
                    <div className="h-4 w-4 mr-2 animate-spin border-2 border-current border-t-transparent rounded-full" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Publish
                </Button>
              ) : null}
            </>
          ) : showApproveButton ? (
            <Button
              onClick={handleApprove}
              disabled={publishing}
              className="bg-green-600 hover:bg-green-700"
              data-testid="approve-btn"
            >
              {publishing ? (
                <div className="h-4 w-4 mr-2 animate-spin border-2 border-current border-t-transparent rounded-full" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Approve &amp; Publish
            </Button>
          ) : null}
        </div>
      </div>

      {/* Save Draft Confirmation Dialog */}
      <AlertDialog open={showSaveDraftDialog} onOpenChange={setShowSaveDraftDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save as Draft?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingSaveAction === 'return-to-stocktake'
                ? 'Your report data will be saved as a draft before returning to the linked Stocktake session.'
                : 'Your report data will be saved as a draft. You can return to edit it later from the reports repository.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSaveDraft}>
              Save Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Publish Confirmation Dialog */}
      <AlertDialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish {scopeInfo.label} Summary Stock Audit Report?</AlertDialogTitle>
            <AlertDialogDescription>
              Once published, this report will become read-only and will automatically appear in the reports repository.
              {hasUnsavedChanges && (
                <span className="block mt-2 text-amber-600">
                  Your unsaved changes will be saved before publishing.
                </span>
              )}
              {missingUploads.length > 0 && (
                <span className="block mt-3 p-2 bg-rose-50 border border-rose-200 rounded text-rose-700 text-sm">
                  <strong>{summaryLinkStatus?.linked ? 'Blocking before publish:' : 'Missing required uploads:'}</strong>
                  <ul className="mt-1 list-disc list-inside">
                    {missingUploads.map(f => <li key={f}>{f}</li>)}
                  </ul>
                  {summaryLinkStatus?.linked
                    ? 'Resolve the Stock Audit Report above before publishing.'
                    : 'Please upload these files before publishing.'}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePublish} disabled={publishing || missingUploads.length > 0}>
              {publishing ? 'Publishing...' : 'Publish'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Scope C — confirm-first-publish-Stocktake. Shown instead of a
          dead-end error when this report's source Stocktake is only
          'completed' (draft-stage). Publishes the Stocktake through its
          OWN existing endpoint/permission/completeness checks; only on
          success does the original publish/submit/approve action retry.
          If the Stocktake publish fails, the Summary stays an
          unfinalised draft — nothing here changes it. */}
      <AlertDialog open={showPublishStocktakeFirstDialog} onOpenChange={(open) => { if (!open && !publishingStocktakeFirst) { setShowPublishStocktakeFirstDialog(false); setPendingStocktakeGate(null); } }}>
        <AlertDialogContent data-testid="publish-stocktake-first-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Publish the source Stocktake first?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingStocktakeGate?.message || (
                <>This Summary's source Stocktake {pendingStocktakeGate?.session_reference ? `(${pendingStocktakeGate.session_reference})` : ''} is not published yet. It must be published (locked) before this Summary can be published.</>
              )}
              <span className="block mt-2 text-amber-700">
                This will finalise the Stocktake session itself — it stays subject to its own existing completeness and permission checks.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishingStocktakeFirst} data-testid="publish-stocktake-first-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPublishStocktakeFirst} disabled={publishingStocktakeFirst} data-testid="publish-stocktake-first-confirm-btn">
              {publishingStocktakeFirst ? 'Publishing Stocktake...' : 'Publish Stocktake & Continue'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {scopeInfo.label} Summary Stock Audit Report?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The report will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unpublish Confirmation Dialog */}
      <AlertDialog open={showUnpublishDialog} onOpenChange={setShowUnpublishDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unpublish {scopeInfo.label} Summary Stock Audit Report?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revert the report to Draft status. It will no longer be visible to client portal users until re-published. You will be able to edit or delete the report.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unpublishing}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleUnpublish} 
              disabled={unpublishing}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {unpublishing ? 'Unpublishing...' : 'Unpublish'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Return to Auditor Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={(open) => { if (!open) { setShowReturnDialog(false); setReturnComments(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Return Report to Auditor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              The report will be returned to Draft status and the auditor will be notified via email.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="return-comments">Comments (optional)</Label>
              <Textarea
                id="return-comments"
                placeholder="Describe what changes are needed..."
                value={returnComments}
                onChange={(e) => setReturnComments(e.target.value)}
                rows={3}
                data-testid="return-comments-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setShowReturnDialog(false); setReturnComments(''); }}>Cancel</Button>
            <Button onClick={handleReturnToAuditor} disabled={returning} className="bg-red-600 hover:bg-red-700" data-testid="confirm-return-btn">
              {returning ? 'Returning...' : 'Return to Auditor'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ManageMasterListDialog
        open={!!manageListCategory}
        onOpenChange={(open) => { if (!open) setManageListCategory(null); }}
        category={manageListCategory}
        onItemsChanged={loadSectionOptions}
      />
    </div>
  );
};

export default SummaryStockAuditReportPage;
